import { Application, UPDATE_PRIORITY, WorkerManager } from 'pixi.js';
import type { World } from './sim/world';
import type { EntityId } from './sim/entity';
import { GameRenderer } from './render/gameRenderer';
import { backdropCameraTile } from './render/spectatorCamera';
import { SpriteAtlasStore } from './render/spriteAtlasStore';
import { SpriteResidency } from './render/spriteResidency';
import { loadHueTable } from './render/hueTable';
import { loadLandTextures } from './render/landArt';
import { loadStaticTextures } from './render/staticsArt';
import { loadOptionalLightTextures } from './render/lightArt';
import { loadOverheadFont } from './render/bakedFont';
import { createTerrain } from './render/terrainLayer';
import { createTerrainMesh } from './render/terrainMesh';
import {
  terrainMeshEnabled,
  useMeshTerrainPath,
  TERRAIN_MESH_STORAGE_KEY,
} from './render/terrainMeshGeometry';
import { createStatics } from './render/staticsLayer';
import { GameClient } from './net/gameClient';
import { PredictedWalker } from './net/predictedWalker';
import type { IsWalkable } from './sim/mobile';
import { HeldMovement, manualRunning } from './input/heldMovement';
import { installContextMenuGuard } from './input/contextMenuGuard';
import { initMobileJoystick } from './input/joystick';
import { loadBindings, matchAction, type Action } from './input/keybindings';
import { directionFromCursor, previewDirectionFromCursor } from './input/mouseMove';
import {
  resolveTarget,
  findPath,
  nextRoute,
  createRepathState,
  type Tile as PathTile,
} from './input/autowalk';
import {
  createPursuitState,
  targetRat,
  cancelPursuit,
  onRatDied,
  pursuitStep,
} from './input/pursuit';
import { pinchDistance, zoomForPinch } from './input/pinchZoom';
import { LONG_PRESS_MS, movedBeyondTolerance } from './input/longPress';
import { walkableForLevel } from './sim/walkability';
import { canMoonwalk, MOONWALK_COOLDOWN_MS } from './sim/moonwalk';
import type { Direction } from './sim/direction';
import {
  runSplash,
  setSplashError,
  showCharacterCreation,
  type SplashIdentity,
} from './splash';
import { makeLeaderboardFetcher } from './net/leaderboardApi';
import { createIgnoreList, shouldSuppressActionAnimation } from './ignoreList';
import { createToolbarGroup } from './toolbar';
import { initProfileGump } from './profileGump';
import { presentAchievementAward } from './achievementAward';
import { initWhatsNew, type Announcement } from './whatsNew';
import { initUpdatePrompt } from './updatePrompt';
import { createUpdateController } from './clientUpdate';
import { initLoading } from './loading';
import { initReconnectOverlay } from './reconnectOverlay';
import { revealGameplayHud } from './gameplayHud';
import { planReconnect, type ReconnectPolicy } from './reconnect';
import { loadHuePalette } from './render/huePalette';
import {
  applyGameCursor,
  applyUiCursor,
  setDirectionalCursor,
  preloadGameCursors,
  prepareWarCursors,
  type CursorMode,
} from './render/gameCursor';
import { initDuelUi } from './duelUi';
import { loadEffectArt, type EffectArt } from './render/effectsArt';
import { AssetLoadCoordinator } from './render/assetLoadCoordinator';
import { loadBloodTextures } from './render/bloodArt';
import { sameMapContent, type GameMap } from './sim/map';
import { AudioManager } from './audio/audioManager';
import { SoundObserver } from './audio/soundObserver';
import { initSound } from './sound';
import { initAlwaysRun } from './alwaysRun';
import { initLowPowerMode, liveFrameRate } from './lowPowerMode';
import { MusicPlayer } from './audio/musicPlayer';
import { initSettings } from './settings';
import {
  ClientEventHub,
  ContextMenuRegistry,
  ActionRegistry,
  ChatCommandRegistry,
  type FeatureContext,
  type SharedState,
  type TargetPurpose,
  type RendererExtras,
} from './features/featureContext';
import { isPlausibleMatchTarget } from './features/pvpTarget';
import { isIgnorableScenery } from './features/ignoreTarget';
import { installMusic } from './features/installMusic';
import { installHud } from './features/installHud';
import { installLeaderboard } from './features/installLeaderboard';
import { installWardrobe } from './features/installWardrobe';
import { installPaperdoll } from './features/installPaperdoll';
import { installChat } from './features/installChat';
import { installIgnore } from './features/installIgnore';
import { installEmotes } from './features/installEmotes';
import { installDeath } from './features/installDeath';
import { installBlood } from './features/installBlood';
import { installAdmin } from './features/installAdmin';
import { installDice } from './features/installDice';
import { installDuel } from './features/installDuel';
import { installSphere } from './features/installSphere';
import { installSwarm } from './features/installSwarm';
import { installStorm } from './features/installStorm';
import { installArena } from './features/installArena';
import { installCards } from './features/installCards';
import { installCardDuel } from './features/installCardDuel';
import { installEscort } from './features/installEscort';
import { installJustice } from './features/installJustice';
import { installMoongate } from './features/installMoongate';
import { installDungeon } from './features/installDungeon';
import { createBrowserClientPerformanceMonitor } from './performance/browserClientPerformance';
import { PresentationGovernor } from './performance/presentationGovernor';
import type { PresentationQualityTier } from './performance/clientPerformance';
import {
  PresentationCadence,
  type PresentationCadenceDecision,
  type PresentationCadenceState,
} from './performance/presentationCadence';

/**
 * Browser bootstrap: the client is a *thin client* of the authoritative server,
 * not the owner of the world.
 *
 * The dependency direction is unchanged - input and the renderer talk to a
 * `World`, the `World` talks to neither - but that `World` is no longer something
 * the browser ticks. It is a *read-model* reconstructed from the server's snapshot
 * stream by {@link GameClient}, and player input never enqueues locally: it is
 * wrapped as a sim `Command` and sent up the socket, where the server validates
 * ownership, enqueues it onto the one authoritative queue, ticks, and broadcasts
 * the result back. Keypress -> command -> server tick -> snapshot -> render.
 *
 * The local player is **predicted** (UO-style per-step movement): a
 * {@link PredictedWalker} - the same sim `Mobile` state machine the server runs,
 * driven from the live input each frame - moves the character instantly and
 * emits one sequence-numbered `Walk` request per turn/step, which the server
 * acks or rejects per step (`onMoveAck`/`onMoveReject`). Every *other* character
 * still renders from the authoritative snapshot stream.
 *
 * The simulation/presentation split is intact: nothing here mutates the
 * read-model world, and the only wall clock read on the client is the render
 * interpolation timer (the sim's clock lives on the server).
 *
 * Since Chassis Slice G (PRD #534 D6, ADR-0268) `main()` is a **bootstrap**:
 * app/assets/socket/movement-prediction plus one {@link FeatureContext}, with
 * every feature (duel, dice, storm, swarm, arena, ...) wired by its own
 * `installX(ctx)` module under `src/features/`. Per-feature `GameClient`
 * callbacks, context-menu rows, keybinding actions, chat commands, boards, and
 * cues are all registrations on the context - nothing feature-shaped lives in
 * this file's lexical scope anymore.
 */

/** Where the authoritative server listens; override with `VITE_SERVER_URL`. */
const SERVER_URL =
  (import.meta.env.VITE_SERVER_URL as string | undefined) ||
  `ws://${location.hostname || 'localhost'}:8080`;

/**
 * This client bundle's build id (Issue #320), baked at build time from `VITE_BUILD_ID`
 * (a git short SHA) - threaded through both deploy paths exactly like {@link
 * SERVER_URL}. The server reports its own build id in every Welcome; when the two
 * differ on a reconnect, the server has been patched since this page loaded, so we
 * offer (or, on `forceReload`, apply) a refresh onto the new bundle. Empty in dev and
 * whenever the deploy didn't thread it - the compare then silently no-ops (it requires
 * both sides non-empty), which is the intended dev behaviour.
 */
const BUILD_ID = (import.meta.env.VITE_BUILD_ID as string | undefined) || '';
const ARENA_PERFORMANCE_PARAMS = new URLSearchParams(location.search);
const ARENA_PERFORMANCE_SCENARIO = ARENA_PERFORMANCE_PARAMS.has('arenaPerf');
const TOWN_PERFORMANCE_SCENARIO = ARENA_PERFORMANCE_PARAMS.has('townPerf');
const requestedArenaPerformanceFps = Number(
  ARENA_PERFORMANCE_PARAMS.get('arenaPerfFps') ?? 60,
);
const ARENA_PERFORMANCE_ACTIVE_FPS =
  ARENA_PERFORMANCE_SCENARIO &&
  Number.isFinite(requestedArenaPerformanceFps) &&
  requestedArenaPerformanceFps >= 1 &&
  requestedArenaPerformanceFps <= 240
    ? requestedArenaPerformanceFps
    : 60;
const requestedArenaPerformanceSamples = Number(
  ARENA_PERFORMANCE_PARAMS.get('perfSamples') ??
    ARENA_PERFORMANCE_PARAMS.get('arenaPerfSamples') ??
    600,
);
const ARENA_PERFORMANCE_SAMPLE_CAPACITY = Math.max(
  600,
  Math.min(
    120_000,
    Number.isFinite(requestedArenaPerformanceSamples)
      ? Math.floor(requestedArenaPerformanceSamples)
      : 600,
  ),
);

/**
 * The HTTP origin of the same server, for the `GET /leaderboard` route (Lightweight
 * Accounts Slice D, ADR-0043). Derived from {@link SERVER_URL} by swapping the WS
 * scheme for its HTTP peer (`ws`->`http`, `wss`->`https`) so a single override
 * (`VITE_SERVER_URL`) still points both the socket and this fetch at one host.
 */
const LEADERBOARD_URL = `${SERVER_URL.replace(/^ws/, 'http')}/leaderboard`;

/**
 * The one paged leaderboard fetcher (see {@link makeLeaderboardFetcher}), shared by
 * the splash panel here and - through the context - the board window and the Arena
 * Master gump.
 */
const fetchLeaderboardPage = makeLeaderboardFetcher(LEADERBOARD_URL);

/**
 * The HTTP origin's `GET /whatsnew.json` route (Issue #185, ADR-0099): the login
 * "what's new" announcement manifest, derived from {@link SERVER_URL} exactly like
 * {@link LEADERBOARD_URL} so a single `VITE_SERVER_URL` override points it at one host.
 */
const WHATSNEW_URL = `${SERVER_URL.replace(/^ws/, 'http')}/whatsnew.json`;

/** Narrow one raw manifest entry to a well-formed {@link Announcement}, or `null`. */
function toAnnouncement(raw: Partial<Announcement> | null | undefined): Announcement | null {
  // Only a well-formed entry (a non-empty id + title) is worth showing; anything else
  // is treated as "nothing published" so a half-authored file never pops a modal.
  if (typeof raw?.id !== 'string' || raw.id.length === 0) return null;
  if (typeof raw.title !== 'string' || raw.title.length === 0) return null;
  return {
    id: raw.id,
    title: raw.title,
    ...(typeof raw.date === 'string' ? { date: raw.date } : {}),
    ...(typeof raw.body === 'string' ? { body: raw.body } : {}),
  };
}

/**
 * Fetch the "what's new" patch notes (Issue #185) for the post-entry modal. Returns
 * the newest-first list of well-formed pages, or `[]` on any failure or a
 * malformed/empty body (server down, no manifest published, bad JSON) - the modal
 * treats an empty list as "show nothing", so a missing manifest never blocks entry.
 * Accepts either the `{ announcements: [...] }` shape or a bare array.
 */
async function fetchWhatsNew(): Promise<Announcement[]> {
  try {
    const res = await fetch(WHATSNEW_URL);
    if (!res.ok) return [];
    const payload = (await res.json()) as
      | { announcements?: Partial<Announcement>[] }
      | Partial<Announcement>[];
    const raw = Array.isArray(payload) ? payload : payload?.announcements;
    if (!Array.isArray(raw)) return [];
    return raw.map(toAnnouncement).filter((a): a is Announcement => a !== null);
  } catch {
    return [];
  }
}

/**
 * Dev-only door authoring overlay (issue #27), off by default. Enabled with the
 * `?dev=doors` query param or `VITE_DOOR_AUTHOR=1`. When on, the overlay module is
 * dynamically imported (so it is absent from the normal client bundle) and drops a
 * keyboard-driven ghost door into the live scene. It makes no sim changes.
 */
const DOOR_AUTHOR =
  new URLSearchParams(location.search).get('dev') === 'doors' ||
  import.meta.env.VITE_DOOR_AUTHOR === '1';

/**
 * Dev-only coordinate read-out (`?dev=coords`), off by default. Shows the local
 * player's tile and the tile under the cursor as a small HUD, so an author can
 * read off exact `x`/`y` for the `extractMap.ts` sign/door placement lists.
 * Dynamically imported, so it never ships in the normal client bundle.
 */
const COORD_HUD = new URLSearchParams(location.search).get('dev') === 'coords';

/**
 * Render-path toggle for sloped terrain (PRD #536 Slice E), off by default:
 * terraced (Slice C) stays the shipped ground until the mesh path is
 * captain-eyeballed on real art. Opt in per session with `?terrain=mesh` (the
 * `?dev=` convention) or persistently with
 * `localStorage['britbank.terrainMesh'] = '1'`. Graduation path: flip the
 * default in `terrainMeshEnabled` once eyeballed; flat maps keep the sprite
 * path either way (`useMeshTerrainPath`).
 */
const TERRAIN_MESH = terrainMeshEnabled(
  location.search,
  localStorage.getItem(TERRAIN_MESH_STORAGE_KEY),
);

/**
 * Fixed simulation step (ms): the server ticks at 20Hz (50ms/step), the span the parked
 * autowalk re-eval polls at. Snapshots themselves now broadcast at 10Hz (~every 100ms,
 * ADR-0163), decoupled from the sim tick.
 */
const STEP_MS = 50;

/**
 * How long the first-visit loading overlay (src/loading.ts) waits for the world to
 * become renderable (assets loaded **and** the backdrop's first frame drawn) before
 * giving up with an error/retry. A generous ceiling: a healthy load is sub-second,
 * so this only fires when assets or the backdrop connection are genuinely wedged
 * (e.g. the server is reachable enough to open a socket but never sends a snapshot).
 * A hard socket failure surfaces the error sooner, off the socket's `close` event.
 */
const LOAD_TIMEOUT_MS = 20_000;

/**
 * Backoff schedule for reconnecting the spectator backdrop socket (the login-screen
 * preview) when it drops before the world rendered. A transient drop recovers on its
 * own with exponential backoff (250ms, 500ms, 1s, 2s, capped at 2s); the elapsed
 * budget is pinned to {@link LOAD_TIMEOUT_MS} so a *sustained* outage escalates to
 * the blocking error/retry on the same deadline as the no-snapshot backstop, never
 * retrying silently forever. See `src/reconnect.ts` and ADR-0069.
 */
const BACKDROP_RECONNECT_POLICY: ReconnectPolicy = {
  baseDelayMs: 250,
  maxDelayMs: 2_000,
  budgetMs: LOAD_TIMEOUT_MS,
};

/**
 * Backoff schedule for reconnecting the **gameplay** socket when it drops after the
 * splash was submitted (ADR-0111). The same pure {@link planReconnect} decision as
 * the backdrop's, tuned for a live session: the "Reconnecting…" overlay is up over
 * the frozen world, so it retries harder for longer - the cap breathes up to 5s and
 * the budget stretches to 5 minutes, enough to ride out a full server reboot/redeploy
 * (an AFK player reconnects automatically once the server is back up) - before
 * escalating to the blocking error/reload. Each successful Welcome resets the
 * outage clock, so every fresh drop gets the full budget.
 */
const GAME_RECONNECT_POLICY: ReconnectPolicy = {
  baseDelayMs: 250,
  maxDelayMs: 5_000,
  budgetMs: 5 * 60_000,
};

/**
 * Render-loop frame cap for a **live, entered** session (ADR-0132). The ticker does
 * full-world work every rAF frame; uncapped it ran at the monitor's refresh
 * (120/144Hz on many laptops), spending render CPU/GPU far above what a 10Hz
 * (ADR-0163; 20Hz before it) authoritative stream can show. 60fps is smooth for this game and roughly halves
 * render cost on high-refresh displays.
 */
/**
 * Render-loop frame cap while the **login splash is still up** (item 5 / ADR-0135).
 * Before entry the same ticker drives only the read-only spectator backdrop - the
 * alive bank rendered behind the translucent modal - which is decorative, not
 * interactive. Half the live cap keeps that background cheap (it renders a whole
 * scene from a live socket the visitor isn't playing) without any visible cost: a
 * 30fps backdrop behind a modal reads identically. Raised to the selected live rate
 * in `onWelcome` the moment the visitor enters and the loop starts driving *their*
 * character.
 */
const BACKDROP_MAX_FPS = 30;

async function main(): Promise<void> {
  // britbank owns right-click throughout the game window. Install before the
  // first awaited boot work so canvases, gumps, HUDs, and loading overlays all
  // suppress the browser menu while retaining their own contextmenu handlers.
  installContextMenuGuard(document);
  // Publish the dedicated north-pointing gauntlet before any awaited boot work so
  // loading, failure, and retry controls receive the same click cursor as the app.
  applyUiCursor(document.documentElement);

  // The first-visit loading overlay (src/loading.ts) is already painting (declared
  // visible in index.html), covering the empty canvas + splash. Wire its handle now
  // so we can dismiss it on first render and flip it to an error/retry state on a
  // failed load/connect. `loadingSettled` gates the one-shot timer + the fail path
  // so a success can never be clobbered by a stale failure signal and vice-versa.
  const loading = initLoading();
  let loadingSettled = false;
  // Backstop for a connection that opens but never delivers a world: if nothing has
  // rendered by the timeout, surface the error/retry rather than spinning forever. A
  // hard socket failure trips the error sooner (the spectator `close` handler below).
  const dismissLoading = (): void => {
    if (loadingSettled) return;
    loadingSettled = true;
    clearTimeout(loadTimeout);
    loading.ready();
  };
  const failLoading = (message: string): void => {
    if (loadingSettled) return;
    loadingSettled = true;
    clearTimeout(loadTimeout);
    loading.fail(message);
  };
  const loadTimeout = setTimeout(
    () => failLoading('The bank is taking too long to load. Check your connection and try again.'),
    LOAD_TIMEOUT_MS,
  );

  const app = new Application();
  await app.init({
    background: 0x101014,
    resizeTo: window,
    // MSAA buys nothing for dimetric pixel art (every sprite edge is axis-aligned or
    // baked into the art) but costs GPU fill + battery on integrated graphics, so it
    // stays off (ADR-0132). Nearest-neighbour scaling keeps the pixels crisp instead.
    antialias: false,
    preference: 'webgl',
  });
  // Cap the render loop (ADR-0132). While the login splash is up the ticker drives
  // only the decorative spectator backdrop, so it starts at the cheaper
  // BACKDROP_MAX_FPS (item 5 / ADR-0135) and switches to the persisted live-device
  // policy in `onWelcome` when the loop begins driving their own character.
  app.ticker.maxFPS = BACKDROP_MAX_FPS;

  const host = document.getElementById('app');
  if (!host) throw new Error('#app host element missing');
  host.appendChild(app.canvas);

  // Classic UO gauntlet cursor over the viewport (issue #51). Scoped to the `#app`
  // host, so the splash overlay and chat box - siblings, not descendants - keep
  // their native cursors. Purely visual: door clicks and right-click movement (#52)
  // are untouched. Set once up front; while the splash covers the canvas the host
  // is hidden behind it, so the gauntlet only reads once the visitor is in the bank.
  applyGameCursor(host, app);
  // Warm the image cache with all nine gauntlet PNGs (idle + 8 directional) so the
  // first swap to a movement heading is an instant style write, no fetch flash (#66).
  preloadGameCursors();
  // Bake the red war-mode gauntlet variants now (canvas tint -> data-URL, Rat
  // Swarm Slice E) so the first Tab press swaps instantly, no fetch or bake lag.
  // The duel target cursor is a distinct baked asset (UO 0x2076), preloaded above.
  prepareWarCursors();

  // Positional sound - footsteps + door open/close (ADR-0092). Like roof-hiding, it
  // is a per-viewer reaction to the authoritative snapshot stream, never on the
  // wire. Start loading + decoding the baked wavs now (in parallel with the rest of
  // asset loading); the audio context is created suspended and only emits once a
  // user gesture wakes it (browser autoplay policy), so wake it on the first
  // pointer/key event - the splash submit qualifies.
  const audio = new AudioManager();
  // The background-music player (ADR-0146): the in-browser General-MIDI synth that
  // streams the baked UO login playlist. It shares the AudioManager's single
  // AudioContext (its own gain node, independent of the SFX master), so build it once
  // the context exists. init() best-effort loads the soundfont + worklet + tracks and
  // is a silent no-op if any are absent; the playlist is started on login (the music
  // installer's Welcome hook).
  const music = new MusicPlayer();
  // Stop the synth + free its nodes on page unload, so nothing leaks on navigation.
  window.addEventListener('pagehide', () => music.dispose());
  // Audio loading is deferred to the first user gesture: nothing is audible until the
  // player enters (autoplay policy; music starts on Welcome), so fetching the ~8MB
  // soundfont + the sound buffers at page load would only steal bandwidth from the visual
  // asset load on the splash's critical path. On that first gesture we open the shared
  // context, kick off music (soundfont) and the sound buffers in PARALLEL - music no
  // longer waits behind the larger sound-buffer decode - and resume the context (the
  // gesture satisfies autoplay). Idempotent: audio.open/load and music.init each guard a
  // repeat, so pointerdown and keydown both firing (or a later resume) is harmless.
  let audioStarted = false;
  const wakeAudio = (): void => {
    if (!audioStarted) {
      audioStarted = true;
      const ctx = audio.open();
      if (ctx) void music.init(ctx);
      void audio.load();
    }
    audio.resume(); // after open(), so the freshly-created context is resumed too
  };
  window.addEventListener('pointerdown', wakeAudio, { once: true });
  window.addEventListener('keydown', wakeAudio, { once: true });

  // The sound controls (mute + volume, in the settings panel - ADR-0105).
  // Initialised here so the persisted mute choice is applied to the audio master up
  // front - including before `audio.load()` resolves, which `AudioManager` handles by
  // remembering the flag.
  initSound({
    onMuteChange: (muted) => audio.setMuted(muted),
    onVolumeChange: (level) => audio.setVolume(level),
  });

  // The lazy sprite-atlas store (ADR-0247 / issue #474): body + equipment PNG atlases
  // stream in on first sighting rather than eager-loading all ~95 MB at boot. Only the
  // login-critical body set (human 400/401 + ghost aliases) is preloaded up front here;
  // the creature bestiary and every equipment layer load on demand as the crowd renders.
  const assetLoads = new AssetLoadCoordinator(() => WorkerManager.reset());
  window.addEventListener('pagehide', () => assetLoads.dispose());
  // Bounded sprite residency (PRD #624, issue #625): streamed atlases carry byte
  // weights and are evicted LRU-first past a soft budget; visible/corpse/preview
  // looks pin theirs. The budget mode follows the ADR-0308 low-power setting below.
  const spriteResidency = new SpriteResidency();
  const atlasStore = new SpriteAtlasStore(assetLoads, spriteResidency);
  const effectArt: EffectArt = {};

  // Static art atlases are independent of which map the server sends, so load them
  // up front in parallel (while the visitor fills in the splash); terrain/statics
  // geometry is built from the map that arrives in the first snapshot. Only the
  // essential body set (400/401 + ghosts) is loaded here so the visitor can render
  // themselves and the human crowd; other bodies/equipment stream in lazily.
  const assets = assetLoads.run(() =>
    Promise.all([
      atlasStore.preloadEssentials(),
      loadHueTable(),
      loadLandTextures(),
      loadStaticTextures(),
      loadOptionalLightTextures(),
      loadHuePalette(),
      // Register the baked UO overhead font (names + speech) before any PlayerSprite
      // - including the splash preview - so the first label resolves the BitmapFont.
      loadOverheadFont(),
      // The blood-splatter decal atlas (PRD #372, Slice A); an absent/partial bake
      // loads as an empty/partial map and the layer draws Graphics-blob fallbacks.
      loadBloodTextures(),
    ]),
  );

  // The login "what's new" modal (Issue #185, ADR-0099): fetch the announcement
  // manifest now, in parallel with asset load (fire-and-forget,
  // `null` on any failure so an outage never blocks entry), and stash the result. It
  // is consulted once at the Welcome moment (below) - not over the splash - so the
  // announcement never competes with the login form. `initWhatsNew` wires the modal's
  // DOM (dismiss button, backdrop click, Escape) up front while its markup is static.
  const whatsNew = initWhatsNew();
  let pendingAnnouncements: Announcement[] = [];
  void fetchWhatsNew().then((announcements) => {
    pendingAnnouncements = announcements;
  });

  // The build-freshness check (Issue #320): a fast redeploy only blips the socket, so
  // the page never reloads and players keep running the pre-patch bundle. The server
  // reports its build id in every Welcome; this controller compares it to our baked
  // BUILD_ID on each reconnect and either prompts (default) or force-reloads (a
  // compatibility-breaking deploy). The prompt handle and controller live at page scope
  // so they persist across reconnects (one prompt / one scheduled reload per load); the
  // GameClient callbacks are wired per-connection in connect().
  const updatePrompt = initUpdatePrompt();
  const updateController = createUpdateController({
    localBuildId: BUILD_ID,
    showPrompt: (applyReload) => updatePrompt.show(applyReload),
    reload: (serverBuildId) => {
      // index.html is served no-cache and assets are content-hashed + immutable, so a
      // plain reload already fetches the new shell + bundle; the `?v=<id>` cache-bust is
      // belt-and-suspenders against a CDN/proxy that ignores no-cache on the shell.
      try {
        if (serverBuildId) {
          location.replace(`${location.pathname}?v=${encodeURIComponent(serverBuildId)}`);
        } else {
          location.reload();
        }
      } catch {
        location.reload();
      }
    },
    // Cross-page-load one-shot guard for a *forced* reload (both the forceReload deploy flag
    // and the SCHEMA_VERSION-skew degrade), keyed in sessionStorage so it survives the
    // reload. Without it a bundle that can't self-update (a CDN/proxy ignoring no-cache on
    // the shell and the ?v= cache-bust) would re-force a reload on every reconnect - a
    // whole-room infinite reload loop. The controller clears it once a matching build is
    // seen, so a *later* independent deploy in this same tab can force again. Every access
    // is wrapped because sessionStorage can throw (private mode); on a throw we allow the
    // reload rather than strand the player.
    forcedReloadGuard: {
      taken: () => {
        try {
          return sessionStorage.getItem('britbank.updateReloaded') === '1';
        } catch {
          return false;
        }
      },
      take: () => {
        try {
          sessionStorage.setItem('britbank.updateReloaded', '1');
        } catch {
          /* storage unavailable (private mode); proceed with the reload */
        }
      },
      clear: () => {
        try {
          sessionStorage.removeItem('britbank.updateReloaded');
        } catch {
          /* storage unavailable (private mode); the guard just stays as-is */
        }
      },
    },
  });

  // True once the visitor has entered the bank on the real connection, so the
  // spectator backdrop stops driving the renderer and tears itself down.
  let entered = false;
  let arenaPerformanceScenarioStarted = false;
  const presentationGovernor = new PresentationGovernor();
  let presentationTier: PresentationQualityTier = 'full';
  let presentationReason = 'startup';
  const createCadence = (lowPower: boolean): PresentationCadence =>
    new PresentationCadence({
      activeFps: ARENA_PERFORMANCE_SCENARIO
        ? ARENA_PERFORMANCE_ACTIVE_FPS
        : liveFrameRate(lowPower),
      ambientFps: lowPower ? 20 : 30,
      quiescentFps: 5,
      activeHoldMs: 500,
      ambientHoldMs: 2_000,
    });
  let presentationCadence = createCadence(false);
  let presentationCadenceState: PresentationCadenceState = 'active';
  let presentationCadenceFps = ARENA_PERFORMANCE_SCENARIO
    ? ARENA_PERFORMANCE_ACTIVE_FPS
    : 60;
  const applyCadence = (decision: PresentationCadenceDecision): void => {
    presentationCadenceState = decision.state;
    presentationCadenceFps = decision.fps;
    if (entered && decision.changed) app.ticker.maxFPS = decision.fps;
  };
  const lowPowerMode = initLowPowerMode({
    onChange: (enabled) => {
      presentationCadence = createCadence(enabled);
      if (entered) applyCadence(presentationCadence.wake(performance.now()));
      // The low-power setting also selects the smaller sprite-residency soft budget
      // (issue #625), so a phone sheds unpinned atlases sooner than a desktop.
      spriteResidency.setMode(enabled ? 'low-power' : 'normal');
      const decision = presentationGovernor.setMinimumTier(
        enabled ? 'reduced' : 'full',
        enabled ? 'manual-low-power' : 'manual-low-power-disabled',
        performance.now(),
      );
      presentationTier = decision.tier;
      presentationReason = decision.reason;
    },
  });
  presentationCadence = createCadence(lowPowerMode.isEnabled());
  presentationCadenceFps = ARENA_PERFORMANCE_SCENARIO
    ? ARENA_PERFORMANCE_ACTIVE_FPS
    : liveFrameRate(lowPowerMode.isEnabled());
  // initLowPowerMode fires onChange with the initial state, so the residency's
  // budget mode is already aligned here for both device defaults.
  const performanceMonitor = createBrowserClientPerformanceMonitor({
    build: BUILD_ID || 'development',
    powerMode: () => (lowPowerMode.isEnabled() ? 'low-power' : 'normal'),
    sampleCapacity:
      ARENA_PERFORMANCE_SCENARIO || TOWN_PERFORMANCE_SCENARIO
        ? ARENA_PERFORMANCE_SAMPLE_CAPACITY
        : undefined,
  });
  window.addEventListener('pagehide', () => performanceMonitor.destroy());
  let performanceCameraTile: () => { x: number; y: number } | undefined = () => undefined;
  let setPerformanceCameraTile: (x: number, y: number) => void = () => {};
  let restorePerformanceCamera: () => void = () => {};
  const wakePresentation = (): void => {
    if (entered) applyCadence(presentationCadence.wake(performance.now()));
  };
  window.addEventListener('keydown', wakePresentation, { capture: true });
  window.addEventListener('pointerdown', wakePresentation, { capture: true });
  window.addEventListener('pointermove', wakePresentation, { capture: true });
  window.addEventListener('touchstart', wakePresentation, { capture: true, passive: true });
  window.addEventListener('wheel', wakePresentation, { capture: true, passive: true });
  window.addEventListener('resize', wakePresentation);
  if (import.meta.env.DEV || ARENA_PERFORMANCE_SCENARIO || TOWN_PERFORMANCE_SCENARIO) {
    (
      window as typeof window & {
        __britbankPerformance?: {
          report: () => ReturnType<typeof performanceMonitor.report>;
          presentation: () => ReturnType<typeof performanceMonitor.presentationSnapshot>;
          reset: () => void;
          cameraTile: () => { x: number; y: number } | undefined;
          setCameraTile: (x: number, y: number) => void;
          restoreCamera: () => void;
        };
      }
    ).__britbankPerformance = {
      report: () => performanceMonitor.report(),
      presentation: () => performanceMonitor.presentationSnapshot(),
      reset: () => performanceMonitor.reset(),
      cameraTile: () => performanceCameraTile(),
      setCameraTile: (x, y) => setPerformanceCameraTile(x, y),
      restoreCamera: () => restorePerformanceCamera(),
    };
  }

  // The splash title is a static image logo (public/logo.png, set in index.html);
  // no Pixi render step is needed for it (ADR-0076).

  // Wire the splash form now so it is interactive immediately (its markup is static
  // in index.html); we await the chosen identity at the very end, once assets are
  // loaded and the live-world backdrop is up. `connect` is assigned below and is only
  // ever invoked long after - on first submit (at the end) or on a resubmit retry.
  let connect: (id: SplashIdentity) => void = () => {};
  // The display name the player entered on the splash, kept current across retries
  // - the identity "Jump to your position" (Issue #195) resolves a rank for.
  let viewerName = '';
  const identityPromise = runSplash(document, {
    // On a rejected login the splash re-shows and the visitor resubmits; that retry
    // opens a brand-new connection through `connect`.
    onResubmit: (sel) => connect(sel),
  });

  // Block on the Pixi atlases the renderer needs. A failure here (a missing/corrupt
  // baked asset, a network drop) means the bank can never render, so flip the
  // loading overlay to its error/retry state and stop rather than throwing into a
  // blank screen - the one hard prerequisite the visitor must be told about.
  let loaded: Awaited<typeof assets>;
  try {
    loaded = await assets;
  } catch (err) {
    console.error('[client] asset load failed', err);
    failLoading("Couldn't load the bank's artwork. Check your connection and try again.");
    return;
  }
  const [, hueTable, landTextures, staticTextures, lightTextures, huePalette, , bloodTextures] =
    loaded;
  // The store's frame records are the same objects the renderer and preview surfaces
  // read synchronously; they start with only the essential body set and fill in lazily
  // as atlases stream (the store mutates them and announces each arrival).
  const bodyFrames = atlasStore.bodyFrames;
  const equipFrames = atlasStore.equipFrames;

  // Everything below is built lazily once a Welcome arrives (it carries the map and
  // the local player's EntityId). `activeClient` is the client behind the current,
  // *accepted* connection - reassigned on each retry after a rejection.
  let renderer: GameRenderer | undefined;
  performanceCameraTile = () => renderer?.cameraTile();
  setPerformanceCameraTile = (x, y) => {
    if (!TOWN_PERFORMANCE_SCENARIO) return;
    renderer?.setCameraTile({ x, y });
    wakePresentation();
  };
  let effectArtPromise: Promise<EffectArt> | undefined;
  const ensureEffectArt = (): Promise<EffectArt> => {
    effectArtPromise ??= assetLoads
      .run(() => loadEffectArt())
      .then((loadedArt) => {
        Object.assign(effectArt, loadedArt);
        renderer?.setEffectArt(effectArt);
        return effectArt;
      })
      .catch((error) => {
        console.warn('[client] deferred effect art unavailable', error);
        return effectArt;
      });
    return effectArtPromise;
  };
  // The client-only "Always run" setting (ADR-0243): its own checkbox + localStorage
  // key, read live by the movement ticker below to force the run gait for keyboard and
  // cursor steering (run without Shift / at any cursor distance past the turn zone). No
  // wire/server change - the client already owns the `running` gait flag on each Walk
  // (Shift is client-only, and `handleWalk` never gates on it).
  const alwaysRunSetting = initAlwaysRun();
  // The live connection's positional-sound observer, so the Arena scene swap can
  // reset its per-entity tracking (ADR-0177): entity ids overlap across worlds
  // (ADR-0176's pre-seated run allocator), so a reused id at a different tile
  // would otherwise read as a "step" and fire a spurious proximity footstep at
  // the crossing. Reassigned per connection (each connect() builds a fresh one).
  let activeSoundObserver: SoundObserver | undefined;
  // The map the current renderer's terrain/statics scene was built from, for the
  // mid-session scene swap (Arena entry/exit, ADR-0177): a map-bearing snapshot
  // whose map *differs in content* (see sameMapContent - identity churns on every
  // full snapshot) rebuilds the whole scene from the new world.
  let sceneMap: GameMap | undefined;
  let playerId: EntityId | undefined;
  restorePerformanceCamera = () => {
    if (!TOWN_PERFORMANCE_SCENARIO || playerId === undefined) return;
    renderer?.setCameraTarget(playerId);
    wakePresentation();
  };
  let activeClient: GameClient | undefined;
  // The local player's prediction walker (UO-style per-step movement): built on
  // each Welcome from the owned entity's movement config, driven from the ticker,
  // and re-anchored by acks/rejects/snapshots. Undefined pre-entry (spectator).
  let walker: PredictedWalker | undefined;

  // War mode + rat pursuit (Rat Swarm Slice E, PRD #222, ADR-0120). War mode is a
  // strictly client-local interaction gate: it arms the renderer's rat
  // hover/click wiring and swaps the cursor to the red war gauntlet - nothing is
  // replicated (only the per-stomp attack swing is, server-side). `pursuit` is
  // the pure chase brain (src/input/pursuit.ts), driven each frame from the
  // ticker below through the same autowalk controller taps use. `dyingRats`
  // tracks rats whose `die` one-shot this client has seen but whose corpse
  // entity still lingers (~1s, so the clip can play) - fed by the swarm
  // installer's die-clip handler, read by the pursuit targeting here.
  // `toggleWarMode` is assigned below, once the cursor-state it rewrites exists
  // (mirrors the `connect` forward-declaration pattern).
  let warMode = false;
  // The shared blue target cursor is armed (Player duels, PRD #252, ADR-0130/0139):
  // set when a feature arms a purpose (spoken "I wish to duel", the dice cup menu,
  // `/ignore`), cleared once the player picks a target or cancels. Client-local,
  // like `warMode`; it swaps the cursor to the UO targeting gauntlet (0x2076) and
  // arms the renderer's blue player-hover/click wiring.
  let duelArmed = false;
  // What an armed target cursor is *for* (Dice Wager, ADR-0154; `/ignore`, ADR-0185):
  // the same blue player-hover/click cursor serves a duel, a dice wager, and an
  // `/ignore` squelch pick. The renderer fires one `onDuelTargetClick`; the purpose
  // routes the pick to the arming installer's handler. Reset to `'duel'` on disarm.
  let duelTargetPurpose: TargetPurpose = 'duel';
  // Admin panel state (ADR-0183). `isAdminViewer` is the reveal-only flag the server
  // sends on the Welcome: it decides whether the F10 panel / teleport key appear, and
  // NOTHING more - every admin command is re-authorized on the server against the
  // socket's own passcode-proven identity, so a forced-open panel on a non-admin is
  // powerless. `clientNoClip` mirrors the server's session no-clip flag for movement
  // prediction (so a wall-step doesn't rubber-band); the server stays the sole
  // authority (a mismatched client is snapped back at the wall). `teleportArmed` is the
  // tile-target cursor mode (distinct from the entity duel/ignore cursor) - a click
  // sends AdminTeleport for that tile.
  let isAdminViewer = false;
  let clientNoClip = false;
  let teleportArmed = false;
  // The challenger's durable gold and whether it is durable (reserved), captured from
  // the owner-only GoldUpdate pushes (via the HUD installer) so the wager prompts can
  // validate a stake and limit anonymous sessions to honor duels (zero wager).
  let durableGold = 0;
  let goldIsDurable = false;
  // The local player is in a live duel's combat stance (Duel slice D, ADR-0131),
  // driven by the duel installer off its replicated `renderable.combatStance`.
  // While true the war-red cursor is forced on (overriding the local Tab war-mode
  // toggle) and reverts when the duel ends. Distinct from `warMode` so forcing it
  // never disturbs the player's own stance.
  let duelCombat = false;
  const pursuit = createPursuitState();
  // Whether the active pursuit began from a touch tap (mobile) rather than a
  // desktop click. The walk-up destination marker is a touch affordance, so it
  // rides along on a mobile pursuit but stays hidden on a desktop one (which has
  // no touch destination indicator). Set at each pursuit start.
  let pursuitViaTouch = false;
  const dyingRats = new Set<EntityId>();
  let toggleWarMode: () => void = () => {};

  // The latest read-model world the renderer draws, and when it arrived, so the
  // renderer can interpolate between the last two received snapshots.
  let latestWorld: World | undefined;
  let lastSnapshotAt = 0;

  // The last identity submitted through connect() (re-login + own-body cues).
  let lastIdentity: SplashIdentity | undefined;

  // The top-right toolbar's shared open-state (mutual exclusion, `toolbar.ts`,
  // ADR-0133): opening any one popover panel (wardrobe / notifications / settings)
  // closes whichever sibling was open, so at most one hangs below the corner at a
  // time. Each panel registers itself through the context.
  const toolbarPanels = createToolbarGroup();

  // The per-viewer ignore / squelch list (client-side only): names whose chat is
  // hidden for *this* viewer on both surfaces (overhead bubble + journal line). It
  // persists in localStorage and never touches the socket. Shared state: the chat
  // installer's speech filter reads it; the Settings panel and the ignore
  // installer's `/ignore` pick edit it.
  const ignoreList = createIgnoreList();

  // The duel prompt UI (Player duels, PRD #252, ADR-0130): the challenger's wager
  // prompt and the target's accept/decline window. Built once and shared through
  // the context - the dice wager reuses the same window with dice copy (ADR-0154).
  const duelUi = initDuelUi();

  // The last wall-clock ms (the client's own `performance.now` clock) a moonwalk was
  // triggered locally, mirroring the server's per-session `lastMoonwalkAt` so the client
  // never *predicts* a glide the authority will reject on cooldown and then rubber-bands
  // (ADR-0145). `undefined` until the first moonwalk.
  let lastMoonwalkAt: number | undefined;
  /**
   * Trigger a moonwalk (ADR-0145) - shared by the Ctrl+M hotkey and the `[moonwalk` chat
   * command. Predicts the backward glide locally on the walker for an instant, round-trip-
   * free slide, and sends the single server-authoritative `Moonwalk` command so every
   * client in the room sees it too. Client-side gates mirror the server's so an accepted
   * local prediction always matches the authority: only in the world, never mid-duel
   * (combat stance), not while a glide is already playing, and not inside the cooldown.
   */
  const triggerMoonwalk = (): void => {
    if (playerId === undefined || !walker) return;
    // No moonwalk mid-duel (the server drops it too - it would corrupt combat movement).
    if (latestWorld?.get(playerId)?.renderable?.combatStance === true) return;
    // Don't re-trigger while a glide is already sliding (the walker is busy).
    if (walker.isMoonwalking) return;
    // Mirror the authority's per-player cooldown so a spammed trigger neither chains
    // glides nor predicts one the server will reject.
    const now = performance.now();
    if (!canMoonwalk(lastMoonwalkAt, now, MOONWALK_COOLDOWN_MS)) return;
    lastMoonwalkAt = now;
    // A moonwalk is its own steering mode: drop any interaction walk-up route first so the
    // controller stops driving and the glide isn't clobbered (ADR-0145/0166).
    autowalk.cancel();
    walker.startMoonwalk();
    activeClient?.sendCommand({ kind: 'Moonwalk', entity: playerId });
  };

  /* ------------------------------------------------------------------------- *
   * The FeatureContext: registries + seams the feature installers wire against.
   * ------------------------------------------------------------------------- */

  const hub = new ClientEventHub();
  const actions = new ActionRegistry();
  const chatCommands = new ChatCommandRegistry();
  const menu = new ContextMenuRegistry({
    renderer: () => renderer,
    world: () => latestWorld,
    playerId: () => playerId,
  });
  const frameHooks: ((deltaMS: number) => void)[] = [];
  const frameActivitySources: (() => boolean)[] = [];
  const rendererHooks: ((r: GameRenderer, extras: RendererExtras) => void)[] = [];

  // The pick-dispatch + hover-filter registries behind the one shared blue target
  // cursor: each arming installer registers what a pick of its purpose does, and
  // the ignore installer swaps in its own (not range-limited) eligibility filter.
  const targetPickHandlers = new Map<TargetPurpose, (target: EntityId) => void>();
  const targetFilters: Record<TargetPurpose, (id: EntityId) => boolean> = {
    duel: (id) => isPlausibleMatchTarget(latestWorld, playerId, id),
    sphere: (id) => isPlausibleMatchTarget(latestWorld, playerId, id),
    dice: (id) => isPlausibleMatchTarget(latestWorld, playerId, id),
    ignore: () => false,
  };

  // Cross-feature sinks installers provide (see SharedState): the HUD's
  // bottom-band suppression and the chat input's focus poll.
  let hudSuppression: (suppressed: boolean) => void = () => {};
  let chatFocusProvider: () => boolean = () => false;

  const state: SharedState = {
    armTargeting: (purpose) => armTargeting(purpose),
    disarmTargeting: () => disarmDuelTargeting(),
    targetPurpose: () => duelTargetPurpose,
    onTargetPick: (purpose, fn) => {
      targetPickHandlers.set(purpose, fn);
    },
    setTargetFilter: (purpose, fn) => {
      targetFilters[purpose] = fn;
    },
    gold: () => ({ amount: durableGold, durable: goldIsDurable }),
    setGold: (amount, durable) => {
      durableGold = amount;
      goldIsDurable = durable;
    },
    duelUi,
    ignoreList,
    dyingRats,
    ratDied: (rat) => {
      if (onRatDied(pursuit, rat)) autowalk.cancel();
    },
    setDuelCombat: (inCombat) => {
      // Repaint immediately (the ticker only rewrites the cursor on a heading change).
      duelCombat = inCombat;
      setDirectionalCursor(host, lastCursorDir, cursorMode());
    },
    isAdminViewer: () => isAdminViewer,
    setNoClip: (enabled) => {
      // Mirror the flag locally for movement prediction (avoid a wall-step
      // rubber-band), then send the intent. The server is authoritative: if this
      // connection is not a real admin the command is a logged no-op and the
      // wall-step is rejected/snapped.
      clientNoClip = enabled;
      activeClient?.setAdminNoClip(enabled);
    },
    armTeleport: () => armTeleportTargeting(),
    disarmTeleport: () => disarmTeleportTargeting(),
    setHudSuppressed: (suppressed) => hudSuppression(suppressed),
    provideHudSuppression: (fn) => {
      hudSuppression = fn;
    },
    chatInputFocused: () => chatFocusProvider(),
    provideChatFocus: (fn) => {
      chatFocusProvider = fn;
    },
    faceCast: (dir) => walker?.faceCast(dir),
    rushDungeon: (end, dir, durationMs) =>
      walker?.startDungeonRush(end, dir, durationMs),
    localMovementPose: () =>
      walker
        ? {
            ...walker.actionPosition(),
            z: walker.z,
            facing: walker.facing,
          }
        : undefined,
    observeDungeonMovement: (movement) => {
      if (walker && latestWorld) {
        walker.observeDungeonMovement(movement, latestWorld);
      }
    },
    dungeonMovementCheckpoint: () =>
      walker?.dungeonMovementCheckpoint(),
  };

  // The live hotkey dispatch table: settings rebinds swap it (below), the dispatch
  // and the context's `bindings()` seam always read the current one.
  let bindings = loadBindings();

  const ctx: FeatureContext = {
    ui: document,
    canvas: app.canvas,
    audio,
    music,
    assets: { bodyFrames, equipFrames, hueTable, huePalette, atlasStore },
    events: hub,
    menu,
    actions,
    chatCommands,
    toolbar: toolbarPanels,
    fetchLeaderboardPage,
    client: () => activeClient,
    playerId: () => playerId,
    world: () => latestWorld,
    renderer: () => renderer,
    viewerName: () => viewerName,
    identity: () => lastIdentity,
    bindings: () => bindings,
    onFrame: (fn, requiresActivePresentation) => {
      frameHooks.push(fn);
      if (requiresActivePresentation) frameActivitySources.push(requiresActivePresentation);
    },
    onRendererBuilt: (fn) => rendererHooks.push(fn),
    state,
  };

  // Bootstrap-owned registrations (movement-core actions + the `[moonwalk` chat
  // command - movement features, not installable ones).
  actions.register('all-names', {
    down: () => renderer?.setForceNamesVisible(true),
    up: () => renderer?.setForceNamesVisible(false),
  });
  actions.register('war-mode', {
    down: () => toggleWarMode(),
  });
  // Moonwalk (ADR-0145): a toggle-style `down` starts one backward glide (no hold
  // semantics). Shares the exact path the `[moonwalk` chat command uses. All gating
  // (cooldown, no-mid-duel, already-gliding) lives in `triggerMoonwalk`.
  actions.register('moonwalk', {
    down: () => triggerMoonwalk(),
  });
  // `[moonwalk` is a client-side command, not speech (ADR-0145): trigger the glide
  // and consume it so it never broadcasts as a chat line, exactly like `/dress`.
  chatCommands.intercept((text) => {
    if (text.trim().toLowerCase() !== '[moonwalk') return false;
    triggerMoonwalk();
    return true;
  });

  // The settings cog + rebinding gump (issue #184). It owns its working copy of the
  // bindings and calls back on every change, so the live dispatch table (`bindings`,
  // declared above the context) tracks a rebind/reset with no reload.
  const settings = initSettings({
    bindings,
    ignoreList,
    onChange: (next) => {
      bindings = next;
    },
    onOpenChange: (open) => toolbarPanels.setOpen('settings', open),
  });
  toolbarPanels.register('settings', () => settings.setOpen(false));
  const profileGump = initProfileGump(
    document,
    (titleId) => activeClient?.selectTitle(titleId),
    { onOpenChange: (open) => toolbarPanels.setOpen('profile', open) },
  );
  toolbarPanels.register('profile', () => profileGump.setOpen(false));

  /* ------------------------------------------------------------------------- *
   * Install the features (PRD #534 D6, ADR-0268). Each installer registers its
   * GameClient callbacks, menu rows, actions, chat commands, boards, and cues
   * against the context above; install order pins registration order where it
   * matters (dice's "Roll the dice" row before duel's "Duel <name>" row; the HUD's
   * suppression sink before the arena installer that drives it).
   * ------------------------------------------------------------------------- */
  installMusic(ctx);
  installHud(ctx);
  installLeaderboard(ctx);
  installWardrobe(ctx);
  installPaperdoll(ctx);
  installChat(ctx);
  installIgnore(ctx);
  installEmotes(ctx);
  installDeath(ctx);
  installBlood(ctx);
  installAdmin(ctx);
  installDice(ctx);
  installDuel(ctx);
  installSphere(ctx);
  installSwarm(ctx);
  installStorm(ctx);
  installArena(ctx);
  installCards(ctx);
  installCardDuel(ctx);
  installEscort(ctx);
  installJustice(ctx);
  installMoongate(ctx);
  installDungeon(ctx);

  // Build the renderer + dev overlays once, from the first accepted Welcome. The
  // map travels in the snapshot, so the read-model world already wraps a live
  // GameMap; build the terrain/statics geometry straight from it.
  // Build the scene graph from a world's map. The camera is set by the caller:
  // the spectator backdrop aims it at the player spawn tile ({@link setCameraTile}); the real
  // login locks it onto the local player ({@link setCameraTarget}).
  const buildRenderer = (world: World): void => {
    const map = world.map;
    sceneMap = map;
    // Sloped-mesh ground (PRD #536 Slice E), opt-in and heighted-maps-only:
    // a flat map keeps the sprite path even with the toggle on (a flat mesh
    // would land pixel-exact where the sprites do, so sprites ARE the
    // identical output), and open plains stay procedural.
    const terrain = useMeshTerrainPath(TERRAIN_MESH, map)
      ? createTerrainMesh(map, landTextures)
      : createTerrain(map, landTextures);
    const statics = createStatics(map, staticTextures);
    renderer = new GameRenderer(
      app,
      terrain.container,
      statics,
      bodyFrames,
      hueTable,
      equipFrames,
      staticTextures,
      terrain.tiles,
      atlasStore,
      lightTextures,
    );
    renderer.setAuthoredDungeonLighting(map.toData().lighting === 'dungeon');
    renderer.setPerformanceMonitor(performanceMonitor);
    // Scene-shape sampling walks the display tree, every sprite's layer stack, and
    // the terrain placement list once a second. Only the benchmarks and the dev
    // diagnostic read those numbers, so ordinary players never pay for them
    // (PRD #826); the governor's own inputs are sampled either way.
    renderer.setDetailedWorkloadSampling(
      import.meta.env.DEV || ARENA_PERFORMANCE_SCENARIO || TOWN_PERFORMANCE_SCENARIO,
    );
    renderer.onPresentationInvalidated = wakePresentation;
    renderer.setPresentationQuality(presentationTier);
    // Open-plain ground (PRD #387): the arena's endless field ships no grid, so
    // its terrain is the camera-windowed procedural layer the renderer drives.
    renderer.proceduralTerrain = 'procedural' in terrain ? terrain.procedural : undefined;
    renderer.windowedTerrain = 'windowed' in terrain ? terrain.windowed : undefined;
    renderer.meshTerrain = 'cull' in terrain ? terrain : undefined;
    // The fire-mage effect strips (ADR-0181): late-bound so every scene swap's
    // fresh renderer gets them; absent strips degrade to primitives.
    renderer.onEffectArtNeeded = ensureEffectArt;
    renderer.setEffectArt(effectArt);
    // The blood-splatter atlas (PRD #372, Slice A): late-bound so every scene
    // swap's fresh renderer gets it; the "Show blood" gate is the blood
    // installer's renderer hook.
    renderer.setBloodArt(bloodTextures);

    // Clicking a door enqueues a UseDoor up the socket - intent only, never a
    // local mutation. The server validates ownership and toggles the shared door
    // state, which comes back in the next snapshot for every connected player.
    renderer.onDoorUse = (door) => {
      if (playerId !== undefined) {
        activeClient?.sendCommand({ kind: 'UseDoor', entity: playerId, door });
      }
    };

    // A left-click that was really a **long-press** already opened the context menu
    // (ADR-0169), so the ensuing sprite `click` must be swallowed rather than starting a
    // pursuit / picking a duel target. `suppressClick` is the latch the renderer's `click`
    // handlers consult (replacing the old `e.shiftKey` skip); `longPress.opened` is set by the
    // long-press timer below and cleared on the next press.
    renderer.suppressClick = () => longPress.opened;

    // War-mode rat targeting (Rat Swarm Slice E, ADR-0120): a left-click on a rat
    // while war mode is on commits a pursuit - the ticker below chases the rat's
    // live tile and fires the one Stomp on arrival. Target selection refuses a
    // lingering corpse (`dyingRats`), the same predicate that keeps the renderer
    // from highlighting one. The renderer's own war gate already filters
    // peace-mode clicks; the `warMode` re-check here is belt-and-suspenders.
    renderer.onRatClick = (rat) => {
      if (!warMode || playerId === undefined) return;
      pursuitViaTouch = false; // a desktop left-click: no walk-up marker
      targetRat(pursuit, rat, dyingRats);
    };
    renderer.ratTargetable = (rat) => !dyingRats.has(rat);
    // The renderer may be built after the player already toggled war mode on
    // (e.g. a reconnect rebuilding mid-fight): hand it the live gate state.
    renderer.setWarMode(warMode);

    // Target selection on the shared blue cursor (Player duels ADR-0130 / Dice
    // Wager ADR-0154 / `/ignore` ADR-0185): a left-click on an eligible sprite
    // while the cursor is armed picks them - disarm the cursor and route the pick
    // to whichever installer armed the current purpose (duel/dice open their wager
    // prompts; ignore squelches). The server rules authoritatively where a command
    // results; an invalid pick fizzles silently.
    renderer.onDuelTargetClick = (target) => {
      if (!duelArmed || playerId === undefined) return;
      const purpose = duelTargetPurpose;
      disarmDuelTargeting();
      targetPickHandlers.get(purpose)?.(target);
    };
    // The blue-hover/click eligibility filter (best-effort; the server rules for real).
    // It is purpose-aware: a duel/dice cursor uses the range-limited player-vs-player
    // match filter (`isPlausibleMatchTarget`, shared with the long-press menu rows),
    // but an `/ignore` cursor (ADR-0185) swaps in `isIgnorableTarget` - **not**
    // range-limited and not NPC-gated, so you can squelch anyone named in view, near
    // or far. (Rats and event bodies carry no duel-target wiring on their sprites;
    // scenery-role mobiles carry only the ignore-gated wiring below - so neither
    // cursor can ever duel-pick them.)
    renderer.duelTargetable = (id) => targetFilters[duelTargetPurpose](id);
    // The `/ignore` cursor's scenery gate (ADR-0185/ADR-0266): roster NPCs are
    // scenery-role now, so their hover/pick wiring answers to this predicate -
    // true ONLY under an armed ignore purpose, so a duel/dice cursor never
    // highlights or picks an NPC (the false affordance D5 removed stays removed).
    renderer.ignoreTargetable = (id) =>
      duelTargetPurpose === 'ignore' &&
      targetFilters.ignore(id) &&
      isIgnorableScenery(latestWorld, id);
    // A renderer rebuilt while the target cursor is armed (a reconnect) inherits the gate.
    renderer.setDuelTargeting(duelArmed);

    // Per-feature sprite/scene wiring (double-click handlers, the billboard click,
    // the blood gate, arena FX hooks): each installer re-attaches through its
    // registered renderer hook, so a scene swap or reconnect rebuild rewires
    // everything exactly as this inline block once did.
    for (const hook of rendererHooks) hook(renderer, { statics: statics.placements });

    // Dev door-authoring overlay: load lazily and only when gated on, so it never
    // touches the shipped client. It shares the renderer's `statics` array and
    // `world`, so its ghost depth-sorts through the real pipeline each frame.
    if (DOOR_AUTHOR) {
      void import('./dev/doorAuthor').then(({ startDoorAuthor }) =>
        startDoorAuthor({
          app,
          world: renderer!.world,
          statics: statics.activeBillboards,
          staticTextures,
        }),
      );
    }

    // Dev coordinate read-out: a small HUD of the player's tile and the cursor's
    // tile, for reading off placement coords. Lazily imported, gated, render-only.
    if (COORD_HUD) {
      void import('./dev/coordHud').then(({ startCoordHud }) =>
        startCoordHud({ app, renderer: renderer! }),
      );
    }
  };

  // Mid-session **scene swap** (Arena Slice E, PRD #334 / ADR-0177). Entering (and
  // leaving) an Arena run arrives as a map-bearing `Snapshot` for a *different* map
  // (ADR-0176) - the renderer's terrain/statics geometry, its roof-structure index,
  // and every pooled sprite were built from the *old* map, so drawing the new world
  // on them would paint the arena's horde over the town's floor plan. The fix is
  // wholesale: tear the old scene down (renderer.destroy) and rebuild it from the
  // new world through the same buildRenderer path a login uses - which also
  // re-derives the roof-hiding structures, re-wires every sprite handler (incl.
  // every installer's renderer hook), and re-hands the live war/duel gate state.
  // Everything else re-anchors through existing seams: the walker snaps to the
  // arena/portal pose on the seq-0 MoveReject the server sends right after the
  // snapshot (the teleporter-landing pattern), the walkability predicate is cached
  // per adopted world so it self-corrects, and the route/pursuit state - planned on
  // the old map - is simply cancelled here.
  const swapScene = (world: World): void => {
    // Cancel old-map movement plans first (autowalk.cancel touches the old
    // renderer's destination marker, so do it before the teardown).
    cancelPursuit(pursuit);
    autowalk.cancel();
    dyingRats.clear();
    // Sound tracking is per-entity-id and ids overlap across worlds: reset so the
    // first arena/town snapshot is a silent "first sighting" pass, not a spurious
    // footstep from an id that jumped tiles at the crossing.
    activeSoundObserver?.reset();
    renderer?.destroy();
    renderer = undefined;
    buildRenderer(world); // reassigns `renderer` (TS can't see through the call)
    const rebuilt = renderer as GameRenderer | undefined;
    if (playerId !== undefined) rebuilt?.setCameraTarget(playerId);
  };

  // Gameplay-socket reconnect (ADR-0111). A drop of the real connection - after
  // the splash was submitted, before or after entry - is a *transient outage until
  // proven otherwise*, exactly like the backdrop's: the session freezes under the
  // "Reconnecting…" overlay and the login handshake re-runs on exponential backoff
  // (the same pure {@link planReconnect} decision, gameplay tunables). A re-Welcome
  // rebuilds the walker/camera through the normal `onWelcome` path and dismisses
  // the overlay; only an outage that outlives the policy budget escalates to the
  // overlay's blocking error/reload.
  const reconnectOverlay = initReconnectOverlay();
  // The newest gameplay socket: a close from any older, superseded socket is not
  // an outage signal (e.g. the previous connection's teardown racing a retry).
  let gameSocket: WebSocket | undefined;
  // Closes seen this outage (drives the backoff) and when the outage began (the
  // elapsed clock the policy escalates on). Both reset by a successful Welcome.
  let reconnectAttempts = 0;
  let outageStartedAt: number | undefined;
  const scheduleReconnect = (): void => {
    // Freeze the dead session first: without a live socket the walker would keep
    // predicting open-loop (walking a character the server no longer steps) and
    // every command would vanish into a closed socket. Dropping both parks the
    // local player where they stand until the re-Welcome re-anchors everything.
    walker = undefined;
    activeClient = undefined;
    if (outageStartedAt === undefined) outageStartedAt = performance.now();
    reconnectAttempts += 1;
    const plan = planReconnect(
      reconnectAttempts,
      performance.now() - outageStartedAt,
      GAME_RECONNECT_POLICY,
    );
    if (!plan.retry) {
      reconnectOverlay.fail('Lost the connection to the bank. Check your connection and retry.');
      return;
    }
    reconnectOverlay.show();
    setTimeout(() => {
      if (lastIdentity) connect(lastIdentity);
    }, plan.delayMs);
  };

  // Open a fresh connection and send the chosen identity in a first `Login` frame
  // (Lightweight Accounts Slice A): identity no longer rides the connect URL. The
  // server answers with a `Welcome` (spawned) or a `Rejected` (the name is taken);
  // on rejection the splash re-shows so the visitor edits the name and retries,
  // which opens a brand-new connection through this same function. See ADR-0038.
  connect = (id: SplashIdentity): void => {
    lastIdentity = id;
    // Remember the typed name for the board window's "Jump to your position"
    // (Issue #195); a rejected login re-runs connect with the edited name.
    viewerName = id.name;
    // A fresh socket is opening: let every installer reset its per-connection edge
    // latches (the replacement for the old per-connect() closure variables).
    hub.emit('connection');
    const socket = new WebSocket(SERVER_URL);
    // The hot world messages arrive as binary frames (ADR-0190); receive them as
    // ArrayBuffers so `client.receive` can decode them positionally. JSON message
    // frames still arrive as strings and are handled unchanged.
    socket.binaryType = 'arraybuffer';
    gameSocket = socket;
    // Set when the server refuses this login: that teardown closes the socket
    // deliberately and the splash flow owns the retry, so the close handler below
    // must not also schedule an automatic reconnect over the re-shown form.
    let rejected = false;
    const client = new GameClient((data) => socket.send(data));
    client.setPerformanceMonitor(performanceMonitor);
    client.onProfile = (profile) => profileGump.setProfile(profile);
    client.onAchievementAward = (achievement) => {
      presentAchievementAward(achievement, {
        notify: (award) => profileGump.award(award),
        play: (cue) => audio.play(cue),
      });
    };
    // A fresh per-connection sound observer, so a reconnect starts from clean
    // tracking (no stray footstep from a stale tile carried across sockets).
    const soundObserver = new SoundObserver();
    activeSoundObserver = soundObserver;
    audio.resume();
    // Identity rides a first Login frame, sent the moment the socket opens.
    socket.addEventListener('open', () =>
      client.login({
        name: id.name,
        // Keep safe defaults on the wire during a rolling deploy: an older server
        // ignores `appearance` but still receives a legitimate skin instead of its
        // blue per-connection fallback. A current server keys the staged flow on
        // the explicit flag.
        sex: id.sex,
        hue: id.hue,
        appearance: id.appearance,
        // No `equip`: characters start naked and a reserved account spawns in its
        // persisted, server-authoritative outfit (Cosmetic Progression Slice C,
        // ADR-0044) - the loadout is never client-supplied at login.
        // The passcode rides the Login message body, never the connect URL, so it
        // stays out of access logs and browser history (Slice B, ADR-0038/0039).
        passcode: id.passcode,
      }),
    );
    // A binary world frame arrives as an ArrayBuffer (binaryType above); a JSON frame
    // as a string. Pass each through unchanged - `decode` dispatches on the type.
    socket.addEventListener('message', (e) =>
      client.receive(typeof e.data === 'string' ? e.data : (e.data as ArrayBuffer)),
    );
    socket.addEventListener('error', () => console.error('[client] socket error'));
    socket.addEventListener('close', () => {
      // Only the newest socket's close is an outage; a superseded connection's
      // teardown and the deliberate close after a rejection are handled flows.
      if (socket !== gameSocket || rejected) return;
      console.warn('[client] disconnected from server; reconnecting');
      profileGump.setUnavailable();
      scheduleReconnect();
    });

    client.onRejected = (reason) => {
      // No entity was created for this login; surface why and let the visitor edit
      // the name and resubmit (which calls connect() again via onResubmit). A
      // rejection can also end an *automatic* reconnect (e.g. another player took
      // the unreserved name during the outage), so drop the reconnecting overlay
      // and the outage clock - the re-shown splash owns the retry from here.
      rejected = true;
      reconnectAttempts = 0;
      outageStartedAt = undefined;
      reconnectOverlay.hide();
      try {
        socket.close();
      } catch {
        /* already closing */
      }
      setSplashError(document, reason);
    };

    client.onCharacterRequired = () => {
      rejected = true;
      try {
        socket.close();
      } catch {
        /* already closing */
      }
      showCharacterCreation(document);
    };

    // Build-id handshake (Issue #320): fired on every Welcome (so every reconnect),
    // before the snapshot is adopted, so a compatibility-breaking deploy that makes the
    // snapshot un-deserializable still routes this stale client into the reload flow.
    client.onServerBuild = (serverBuildId, forceReload) => {
      updateController.onServerBuild(serverBuildId, forceReload);
    };
    // Reveal-only admin signal (ADR-0183), fired on every Welcome: (un)wire the admin
    // UI to match. This grants NO power - the server re-authorizes every admin command
    // against the socket's own proven identity, so even a panel forced open in devtools
    // is powerless on a non-admin. A reconnect that loses admin re-hides everything;
    // the admin installer's panel reveal rides the hub event.
    client.onIsAdmin = (admin) => {
      isAdminViewer = admin;
      // Hide the admin-teleport rebind row from non-admins (cosmetic - the key + server
      // are gated regardless), so admin features aren't advertised in Settings.
      settings.setAdminActionsVisible(admin);
      if (!admin) disarmTeleportTargeting();
      hub.emit('isAdmin', admin);
    };
    // A snapshot the client cannot deserialize (a SCHEMA_VERSION skew after a breaking
    // deploy) used to throw uncaught and strand the player under "Reconnecting…" forever;
    // now it degrades here to a forced refresh. The controller's forcedReloadGuard (above)
    // caps this at one reload per tab session on a bundle that can't self-update, so a
    // misconfigured deploy can't reload-loop.
    client.onVersionSkew = () => {
      updateController.onVersionSkew();
    };

    client.onWelcome = (welcomeId, world) => {
      // The one-shot forced-reload guard is released by the update controller when it sees
      // a *matching* build (onServerBuild, which fires before this on every Welcome) - not
      // here, because a forced reload onto a still-stale bundle also produces a Welcome and
      // clearing on any Welcome would defeat the guard on that loop.
      // The connection is live (again): retire any outage so the next drop starts
      // a fresh backoff/budget, and drop the "Reconnecting…" veil.
      reconnectAttempts = 0;
      outageStartedAt = undefined;
      reconnectOverlay.hide();
      activeClient = client;
      playerId = welcomeId;
      revealGameplayHud();
      // No-clip is session-scoped and default-off server-side (ADR-0183): a reconnect
      // starts a fresh session with no-clip off, so reset the client mirror to match
      // and drop any armed teleport cursor (the admin installer resets its panel
      // button off the same Welcome). `onIsAdmin` (fired just before this on the
      // same Welcome) has already (un)revealed the panel for this connection.
      clientNoClip = false;
      disarmTeleportTargeting();
      // Build the prediction walker from the owned entity's movement config (the
      // same timings the server validates against) and its spawn pose + level.
      // This rebuild is also what **re-asserts held intent after a reconnect**
      // (ADR-0111): movement input is polled, not edge-triggered - the ticker reads
      // `heldInput`/`mouseHeld`/autowalk every frame, all of which survive the
      // outage - so the fresh walker starts stepping (and emitting `Walk` requests
      // on the new socket's fresh seq/epoch) on its very next update. A key held
      // across the drop resumes walking without a wiggle; the server never has a
      // stale heading because per-step movement carries the heading on every step.
      const me = world.get(welcomeId);
      // The walker's z is a LEVEL: a mirrored terrain z (PRD #536 Slice B)
      // normalizes to the ground level 0 so hill steps never read as level
      // changes (which would churn the drift-snap and the walkability level).
      walker = me?.mobile
        ? new PredictedWalker(
            me.mobile.toState(),
            world.map.levelAt(me.mobile.tileX, me.mobile.tileY, me.position?.z),
            me.position ?? me.mobile.toState(),
          )
        : undefined;
      walker?.setDungeonMode(world.map.toData().dungeon !== undefined);
      // The renderer may already exist - the spectator backdrop built it from the
      // same authoritative world. Build it only if it doesn't, then lock the camera
      // onto the local player (replacing the backdrop's fixed spawn-tile view -
      // the same tile, so no jump; issue #193).
      if (!renderer) buildRenderer(world);
      renderer?.setCameraTarget(welcomeId);
      // The loop now drives the player's own live session, not the decorative
      // pre-entry backdrop, so apply the device/user live cap. Coarse-pointer clients
      // default to 31 FPS to stay inside the 33ms low-power budget while reducing
      // sustained CPU/GPU heat; desktop defaults to 60 FPS.
      // Idempotent, so a reconnect's Welcome is harmless.
      // The real connection now drives the renderer, so retire the read-only
      // spectator backdrop: flip `entered` (its onSnapshot bails) and close its socket.
      entered = true;
      applyCadence(presentationCadence.wake(performance.now()));
      try {
        spectatorSocket.close();
      } catch {
        /* already closing */
      }
      // Now that the player is actually in the bank, offer the "what's new" modal
      // (Issue #185, ADR-0113): `maybeShow` is a no-op unless an announcement was
      // fetched and is well-formed - it shows on every login, at most once per page
      // load, so a reconnect re-firing Welcome never re-opens it within the session.
      whatsNew.maybeShow(pendingAnnouncements);
      // Every installer's Welcome hook runs now (the PR #243 reconnect-reset
      // discipline: boards/overlays reset, music starts, speech hue re-sends) -
      // after the bootstrap seams above are live, so hooks may read
      // client()/renderer()/playerId().
      hub.emit('welcome', welcomeId, world);
      if (ARENA_PERFORMANCE_SCENARIO && !arenaPerformanceScenarioStarted) {
        arenaPerformanceScenarioStarted = true;
        client.sendCommand({ kind: 'StartArenaRun', entity: welcomeId });
      }
    };

    client.onSnapshot = (world) => {
      // Arena entry/exit (ADR-0177): a map-bearing snapshot carrying a *different*
      // map means the player crossed worlds - rebuild the scene from it. Content
      // comparison, not identity: every full snapshot (incl. a reconnect's) parses
      // a fresh map object, but only a genuinely different map (town <-> arena)
      // may rebuild. Deltas/keyframes reuse the baseline's map object, so after
      // re-keying sceneMap onto a same-content fresh map (the reconnect case) the
      // identity fast path keeps this free on the 10Hz stream.
      if (renderer && sceneMap && world.map !== sceneMap) {
        if (!sameMapContent(world.map, sceneMap)) swapScene(world);
        else sceneMap = world.map;
      }
      latestWorld = world;
      renderer?.applyReplica(world);
      const snapshotAt = performance.now();
      if (lastSnapshotAt > 0) {
        performanceMonitor.record('snapshot-interarrival', snapshotAt - lastSnapshotAt);
      }
      lastSnapshotAt = snapshotAt;
      // The walker's keyframe drift safety net: with no requests in flight the
      // snapshot's own-entity pose must match the prediction; a difference snaps.
      if (playerId !== undefined && walker) {
        const pos = world.get(playerId)?.position;
        if (pos) {
          const dungeonMovement =
            world.map.toData().dungeon !== undefined;
          if (dungeonMovement !== walker.isDungeonMovement) {
            walker.setDungeonMode(dungeonMovement, {
              x: pos.x,
              y: pos.y,
              z: world.map.levelAt(pos.x, pos.y, pos.z),
              facing: pos.facing,
            });
          }
          // Feed the walker LEVELS, not raw z (PRD #536 Slice B): the mirrored
          // terrain z of a hill walker is still the ground level, so an uphill
          // step never reads as a level change (a same-tile "drift" that would
          // snap-cancel a live route between steps).
          walker.observeSnapshot({
            x: pos.x,
            y: pos.y,
            z: world.map.levelAt(pos.x, pos.y, pos.z),
            facing: pos.facing,
          });
        }
      }
      // Feature snapshot hooks (wardrobe reconcile, death/res detection, the duel
      // combat-stance couplings, dying-rat pruning) run against the adopted world.
      hub.emit('snapshot', world);
      // Derive positional sound from the same snapshot: footsteps for mobiles that
      // stepped and door open/close toggles, each scaled by tile distance from the
      // local player. The observer is the pure decision; the manager plays it.
      // Known trade-off: the local player's own footsteps key off the snapshot, so
      // they lag the predicted motion by ~RTT. Follow-up: emit them from the walker.
      audio.playAll(soundObserver.observe(world, playerId, lastSnapshotAt));
    };
    client.onPresentationInvalidated = (active) => {
      if (!entered) return;
      applyCadence(
        active
          ? presentationCadence.wake(performance.now())
          : presentationCadence.invalidate(performance.now()),
      );
    };

    // The action-animation broadcast (Rat Swarm Slice B, ADR-0116): play the named
    // body clip on the target's pooled sprite in the broadcast facing - once, or the
    // message's `repeat` count for a looping gesture (the dance emote, ADR-0387) -
    // e.g. the stomper's attack swing, a rat's death clip. Ephemeral like
    // speech (off the snapshot stream); an unknown entity or clip is a safe no-op
    // inside the renderer, so a late joiner or unbaked art never errors. Every
    // feature's clip-keyed cues ride the hub event.
    client.onActionAnimation = (msg) => {
      // Per-viewer ignore (client-side only, ADR-0185/0387): an ignored player's
      // *emote* gesture is dropped here so `/ignore` silences a dance spammer on both
      // surfaces - their text and their animation. Only emote clips are ever hidden;
      // a swing, a flinch, a death, a storm/arena clip always plays (see
      // `shouldSuppressActionAnimation`), so nothing a viewer needs to understand the
      // world can be hidden by their own list.
      const actorName = latestWorld?.get(msg.entityId)?.renderable?.name;
      if (
        shouldSuppressActionAnimation(msg, actorName, playerId, (n) => ignoreList.isIgnored(n))
      ) {
        return;
      }
      renderer?.playActionAnimation(
        msg.entityId,
        msg.clip,
        msg.facing,
        msg.repeat ?? 1,
        msg.actionId,
      );
      hub.emit('actionAnimation', msg);
    };

    // The remaining per-feature messages fan out through the hub - each installer
    // registered its handlers at install time.
    client.onSpeech = (speech) => hub.emit('speech', speech);
    client.onChatHistoryRemove = (message) => hub.emit('chatHistoryRemove', message);
    client.onCombatText = (msg) => hub.emit('combatText', msg);
    client.onDungeonRunStatus = (msg) => hub.emit('dungeonRunStatus', msg);
    client.onDungeonCastResult = (msg) => hub.emit('dungeonCastResult', msg);
    client.onSoulstoneRoster = (msg) => hub.emit('soulstoneRoster', msg);
    client.onOnlineTime = (totalOnlineMs) => hub.emit('onlineTime', totalOnlineMs);
    client.onGoldUpdate = (gold, durable) => hub.emit('goldUpdate', gold, durable);
    client.onDuelPrompt = (msg) => hub.emit('duelPrompt', msg);
    client.onDuelCombat = (msg) => hub.emit('duelCombat', msg);
    client.onSpherePrompt = (msg) => hub.emit('spherePrompt', msg);
    client.onSphereState = (msg) => hub.emit('sphereState', msg);
    client.onSphereEvent = (msg) => hub.emit('sphereEvent', msg);
    client.onSphereCommandResult = (msg) => hub.emit('sphereCommandResult', msg);
    client.onDiceWagerPrompt = (msg) => hub.emit('diceWagerPrompt', msg);
    client.onCardDuelPrompt = (msg) => hub.emit('cardDuelPrompt', msg);
    client.onCardDuelListings = (msg) => hub.emit('cardDuelListings', msg);
    client.onCardDuelListingAccepted = (msg) => hub.emit('cardDuelListingAccepted', msg);
    client.onCardDuelState = (msg) => hub.emit('cardDuelState', msg);
    client.onRatSwarmStatus = (msg) => hub.emit('ratSwarmStatus', msg);
    client.onStormStatus = (msg) => hub.emit('stormStatus', msg);
    client.onStormTelegraph = (msg) => hub.emit('stormTelegraph', msg);
    client.onDungeonTelegraph = (msg) => hub.emit('dungeonTelegraph', msg);
    client.onJusticeFx = (msg) => hub.emit('justiceFx', msg);
    client.onBloodFx = (msg) => hub.emit('bloodFx', msg);
    client.onClearCorpses = () => hub.emit('clearCorpses');
    client.onCollection = (unlocked) => hub.emit('collection', unlocked);
    client.onCardState = (msg) => hub.emit('cardState', msg);
    client.onCardsEnabled = (enabled) => hub.emit('cardsEnabled', enabled);
    client.onPackOpened = (cards, duplicateGold) => hub.emit('packOpened', cards, duplicateGold);
    client.onAutoEquipState = (enabled) => hub.emit('autoEquipState', enabled);
    client.onWardrobeFavoritesState = (state) => hub.emit('wardrobeFavoritesState', state);
    client.onArenaRunStatus = (msg) => {
      if (msg.active) void ensureEffectArt();
      hub.emit('arenaRunStatus', msg);
    };
    client.onArenaDraft = (msg) => hub.emit('arenaDraft', msg);
    client.onArenaResults = (msg) => hub.emit('arenaResults', msg);
    client.onArenaRecord = (msg) => hub.emit('arenaRecord', msg);
    client.onArenaFx = (msg) => hub.emit('arenaFx', msg);
    client.onArenaWarning = (msg) => hub.emit('arenaWarning', msg);
    client.onArenaHistoryPage = (msg) => hub.emit('arenaHistoryPage', msg);

    // Per-step movement control (off-snapshot): an ack pops the walker's pending
    // queue; a reject (or a seq-0 server reposition, e.g. a teleporter landing)
    // clears it and snaps the prediction to the carried authoritative pose.
    client.onMoveAck = (seq) => walker?.onAck(seq);
    // The carried pose's z normalizes to a LEVEL (PRD #536 Slice B): a reject on
    // heighted ground carries the mirrored terrain z, which is still level 0.
    client.onMoveReject = (msg) =>
      walker?.onReject(
        latestWorld ? { ...msg, z: latestWorld.map.levelAt(msg.x, msg.y, msg.z) } : msg,
      );
  };

  // The live-world backdrop (login screen): a **read-only spectator** connection so
  // the alive bank renders on the `#app` canvas behind the translucent splash. It
  // spawns no entity (invisible to every player) and just streams the world; the
  // first snapshot builds the renderer with a fixed camera aimed at the exact player
  // spawn tile (issue #193), so the world behind the modal is already framed where the
  // visitor will appear. It is retired in `onWelcome` the moment the visitor enters on
  // the real connection (which retargets the camera to the local player - the same
  // tile, so no jump - and closes this socket).
  //
  // A *transient* drop before the world renders must not strand a visitor who could
  // still log in, so the socket reconnects with exponential backoff (the pure
  // {@link planReconnect} policy decides the delay + when to give up). Only a
  // *sustained* outage - the backdrop staying down past the policy budget (pinned to
  // `LOAD_TIMEOUT_MS`) - escalates to the blocking error/retry; the 20s no-snapshot
  // timeout above is the matching backstop for a socket that opens but never streams.
  let spectatorSocket: WebSocket;
  // Closes seen so far (drives the backoff) and when the first attempt began (the
  // elapsed-outage clock the policy escalates on). Reset implicitly by a page reload.
  let spectatorAttempts = 0;
  const spectatorStartedAt = performance.now();
  const connectSpectator = (): void => {
    // A reconnect scheduled before the visitor entered (or before loading settled)
    // may fire just after they did; don't open a stray backdrop socket then.
    if (entered || loadingSettled) return;
    const socket = new WebSocket(SERVER_URL);
    // Binary world frames (ADR-0190) as ArrayBuffers; JSON frames stay strings.
    socket.binaryType = 'arraybuffer';
    spectatorSocket = socket;
    const spectator = new GameClient((data) => socket.send(data));
    spectator.setPerformanceMonitor(performanceMonitor);
    socket.addEventListener('open', () => spectator.spectate());
    socket.addEventListener('message', (e) =>
      spectator.receive(typeof e.data === 'string' ? e.data : (e.data as ArrayBuffer)),
    );
    socket.addEventListener('error', () => console.warn('[client] spectator socket error'));
    socket.addEventListener('close', () => {
      // A close *after* entry is the intentional teardown in `onWelcome`, and a close
      // once loading has settled (the world rendered, or an error already showed)
      // needs no reaction - the backdrop has done its job. In either case, don't
      // reconnect and don't fail.
      if (entered || loadingSettled) return;
      // The backdrop dropped before the world rendered. Reconnect with backoff for a
      // transient drop; only escalate to error/retry once the outage has stayed down
      // past the policy budget (or the next wait would overrun it).
      spectatorAttempts += 1;
      const plan = planReconnect(
        spectatorAttempts,
        performance.now() - spectatorStartedAt,
        BACKDROP_RECONNECT_POLICY,
      );
      if (!plan.retry) {
        failLoading('Could not reach the bank. Check your connection and try again.');
        return;
      }
      setTimeout(connectSpectator, plan.delayMs);
    });
    spectator.onSnapshot = (world) => {
      // The real connection has taken over (its socket is being closed): ignore any
      // late spectator frame so the two streams never both drive the renderer.
      if (entered) return;
      if (!renderer) {
        buildRenderer(world);
        // Frame the backdrop on the exact player spawn tile (issue #193), so entering
        // the bank re-centres on the same tile and the camera never jumps. Falls back
        // to the crowd centroid on maps that declare no spawn point.
        renderer!.setCameraTile(backdropCameraTile(world));
      }
      latestWorld = world;
      renderer?.applyReplica(world);
      lastSnapshotAt = performance.now();
    };
  };
  connectSpectator();

  // Held movement keys, sampled each frame as the desired heading the prediction
  // walker pulls at its own cadence. The server stays authoritative per step: the
  // walker's local moves are predictions the server acks or rejects.
  const heldInput = new HeldMovement();
  const joystick = initMobileJoystick();
  const MOVEMENT_KEYS = new Set([
    'ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight',
    'KeyW', 'KeyA', 'KeyS', 'KeyD',
  ]);
  const RUN_KEYS = new Set(['ShiftLeft', 'ShiftRight']);

  // True while focus sits in any text field - the splash name/passcode inputs (live
  // behind the translucent backdrop before entry) or the chat box. The keydown
  // listener now runs while the login form is up (the live-world backdrop made it
  // reachable pre-entry), so without this gate a W/A/S/D keystroke would be
  // `preventDefault`ed as a movement key and never reach the field the visitor is
  // typing into. Covers the chat input too, so it subsumes the chat focus poll.
  const isTextEntryFocused = (): boolean => {
    const el = document.activeElement;
    if (!(el instanceof HTMLElement)) return false;
    return el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.isContentEditable;
  };

  // Global action hotkeys (issue #184) run through the keybinding registry: custom
  // bindings load from localStorage merged over the defaults, and the single
  // matchAction dispatch below routes a matched chord to its handler in the shared
  // ActionRegistry (bootstrap + installers each registered theirs). Every handler
  // sits behind the isTextEntryFocused guard, so a hotkey never fires while the
  // player is typing in chat or the splash.
  //
  // The physical key holding each active hold-action down, so its keyup still finds
  // the action to release even after a modifier was let go first (Alt released while
  // F is still held), where the chord would no longer match.
  const heldActions = new Map<string, Action>();
  const releaseAllActions = (): void => {
    for (const action of heldActions.values()) actions.get(action)?.up?.();
    heldActions.clear();
  };
  // Drop *every* held-input latch on focus loss / tab hide. A focus-stealing key combo
  // (alt-tab, ctrl-w, cmd+key on macOS, ...) takes focus mid-press, so the matching
  // keyup/mouseup lands on another window and never reaches us - stranding the code in
  // `heldInput` (phantom walk/run) or leaving `mouseHeld` true (phantom right-click
  // autowalk). Both are only ever cleared by their own keyup/mouseup, so without this a
  // swallowed release latches movement until the same key is pressed and cleanly
  // released again. Focus loss = release everything (the standard game behaviour): if
  // the player tabs back still holding a key, they re-press to resume. `mouseHeld` is
  // declared below, closed over here (only ever invoked at event time).
  const releaseAllInput = (): void => {
    heldInput.clear();
    joystick.release();
    mouseHeld = false;
    releaseAllActions();
  };

  window.addEventListener('keydown', (e) => {
    // While a text field is focused, the visitor is typing - into the splash
    // name/passcode fields or the chat box - so movement keys must reach the field
    // as text, not steer the character or get swallowed by `preventDefault` (so
    // typing "wasd" never walks you around or drops letters). This also guards the
    // action hotkeys below, so e.g. Alt+F never fires while typing in chat.
    if (isTextEntryFocused()) return;
    // F10 is the admin panel's fixed key (ADR-0183), owned by the admin installer's
    // own listener - reserved here so a rebound action can never double-fire on it.
    if (e.code === 'F10') return;
    // Escape cancels an armed teleport-target cursor (ADR-0183), consistent with a
    // targeting mode you can back out of; harmless when nothing is armed.
    if (e.code === 'Escape' && teleportArmed) {
      e.preventDefault();
      disarmTeleportTargeting();
      return;
    }
    // Action hotkeys exist only once the visitor is in the world: pre-entry the
    // splash owns the keyboard (Tab focus-walks its controls), and firing e.g. a
    // war-mode toggle without a player would just paint state behind the login
    // card while `preventDefault` broke focus walking.
    const action = playerId !== undefined ? matchAction(e, bindings) : null;
    if (action) {
      e.preventDefault();
      // Ignore auto-repeat so a held key fires `down` once, not on every OS repeat.
      if (!heldActions.has(e.code)) {
        heldActions.set(e.code, action);
        actions.get(action)?.down();
      }
      return;
    }
    if (MOVEMENT_KEYS.has(e.code)) {
      heldInput.press(e.code);
      e.preventDefault();
    } else if (RUN_KEYS.has(e.code)) {
      heldInput.press(e.code);
    }
  });
  window.addEventListener('keyup', (e) => {
    heldInput.release(e.code);
    const action = heldActions.get(e.code);
    if (action) {
      heldActions.delete(e.code);
      actions.get(action)?.up?.();
    }
  });
  // A lost window focus (alt-tab, click away) can swallow the keyup/mouseup, so release
  // every held input on blur - otherwise a movement key stays stuck in `heldInput`
  // (phantom walk/run) or a hold-action stays lit after tabbing away mid-hold. `pagehide`
  // covers the bfcache/navigation-away case where `blur` may not fire.
  window.addEventListener('blur', releaseAllInput);
  window.addEventListener('pagehide', releaseAllInput);

  // The canvas's viewport offset, cached (PRD #826). `getBoundingClientRect` is a
  // forced synchronous layout, and every pointer path below wants the same two
  // numbers to turn a client point into a canvas point - including `mousemove`,
  // the one input stream an Arena player saturates while aiming, where it was
  // costing a layout per mouse event. The offset only moves when the page
  // reflows, so it is computed once and dropped on resize and scroll (capturing,
  // so a scrolling ancestor counts too) rather than re-measured per event.
  let canvasOrigin: { left: number; top: number } | undefined;
  const canvasOffset = (): { left: number; top: number } => {
    if (!canvasOrigin) {
      const rect = app.canvas.getBoundingClientRect();
      canvasOrigin = { left: rect.left, top: rect.top };
    }
    return canvasOrigin;
  };
  const invalidateCanvasOffset = (): void => {
    canvasOrigin = undefined;
  };
  window.addEventListener('resize', invalidateCanvasOffset);
  window.addEventListener('orientationchange', invalidateCanvasOffset);
  window.addEventListener('scroll', invalidateCanvasOffset, { capture: true, passive: true });

  // Mouse-driven movement (classic UO right-click / right-click-hold to walk).
  // Right-clicking the viewport walks the player toward the cursor; holding keeps
  // walking and re-aims as the cursor moves; releasing parks cleanly. Like the
  // keyboard, this only produces *intent*: the ticker below resolves the cursor to
  // an 8-way heading and feeds it to the same prediction walker, so mouse and
  // keyboard coexist through one movement seam with no stop-spam.
  let mouseHeld = false;
  let cursor: { x: number; y: number } | undefined;
  const updateCursor = (e: MouseEvent): void => {
    const r = canvasOffset();
    cursor = { x: e.clientX - r.left, y: e.clientY - r.top };
  };
  // Right-click - with or without shift - is always movement (ADR-0161 reverts
  // ADR-0160's shift+right-click gating). The document-level guard installed at
  // bootstrap suppresses the browser menu across the full game window.
  app.canvas.addEventListener('contextmenu', (e) => {
    // Firefox deliberately ignores `preventDefault` on this event when Shift is held (its
    // user escape-hatch to reach the native menu on sites that hijack right-click),
    // so a shift+right-click pops the OS context menu *anyway* and swallows the
    // matching `mouseup`. Without that release the mouse-walk latch stays set and the
    // player autowalks toward the last cursor forever with no way to stop (the walk is
    // driven purely by `mouseHeld` in the ticker). Drop the latch here so the player
    // parks the instant the menu appears - the standard "release everything when the
    // stop signal may be lost" move (cf. `releaseAllInput` on blur). Only when Shift is
    // held: a plain right-click's menu is genuinely suppressed above, so its `mouseup`
    // arrives normally and hold-to-walk must keep working.
    if (e.shiftKey) mouseHeld = false;
  });
  // The in-world context menu opens on a **long-press of left-click** (ADR-0169, replacing
  // the shift+left-click of ADR-0161 - shift is no longer required). Press-and-hold the left
  // button (or a single touch) still over an entity for `LONG_PRESS_MS`; if it hasn't drifted
  // past `LONG_PRESS_MOVE_PX` by then, the contribution-built context menu (`ctx.menu.open`)
  // opens and the press is marked consumed so the ensuing click/tap does **not** also fire
  // the normal action (rat pursuit, an armed duel-target pick, tap-to-walk). A quick
  // press-and-release stays a plain click.
  //
  // `longPress.opened` is the suppression latch: set when the timer opens a menu, read by the
  // renderer's sprite `click` handlers (`renderer.suppressClick`) so a hold that opened the
  // menu never *also* pursues/picks, and by the touch tap machine so a hold never *also*
  // walks. It resets on the next press (`armLongPress`) and, on touch, once the tap is
  // consumed. The menu opens on the timer (mid-hold, well after this press's `pointerdown`),
  // so its own outside-dismiss listener installs too late to catch the opening press - the
  // release's `click`/`pointerup` is not a `pointerdown`, so it never self-dismisses.
  const longPress = {
    timer: undefined as ReturnType<typeof setTimeout> | undefined,
    start: undefined as { x: number; y: number } | undefined, // canvas px, for the move gate
    opened: false, // a menu opened during this press -> suppress the ensuing click/tap
  };
  const cancelLongPress = (): void => {
    if (longPress.timer !== undefined) {
      clearTimeout(longPress.timer);
      longPress.timer = undefined;
    }
    longPress.start = undefined;
  };
  // Arm a fresh long-press: (cx,cy) canvas px for hit-testing, (clientX,clientY) viewport px
  // for the menu anchor. Clears any prior press and the suppression latch, then schedules the
  // open. `movedBeyondTolerance` (from the move handlers) cancels it if the pointer drifts.
  const armLongPress = (cx: number, cy: number, clientX: number, clientY: number): void => {
    cancelLongPress();
    longPress.opened = false;
    longPress.start = { x: cx, y: cy };
    longPress.timer = setTimeout(() => {
      longPress.timer = undefined;
      // Only latch suppression when a menu actually opened (an entity / a menu-worthy
      // surface was under the press); a hold over empty ground opens nothing and
      // stays a normal press.
      longPress.opened = menu.open({ cx, cy, clientX, clientY });
    }, LONG_PRESS_MS);
  };
  // Desktop long-press (mouse/pen). Touch runs through the tap machine's own arm/cancel/commit
  // below, so this path skips `touch` pointers to avoid opening the menu twice.
  app.canvas.addEventListener('pointerdown', (e) => {
    if (e.pointerType === 'touch') return;
    if (e.button !== 0 || !renderer) return; // left button only
    const r = canvasOffset();
    armLongPress(e.clientX - r.left, e.clientY - r.top, e.clientX, e.clientY);
  });
  app.canvas.addEventListener('pointermove', (e) => {
    if (e.pointerType === 'touch') return;
    if (longPress.start === undefined) return;
    const r = canvasOffset();
    if (movedBeyondTolerance(longPress.start, { x: e.clientX - r.left, y: e.clientY - r.top })) {
      cancelLongPress(); // the press became a drag/steer, not a menu
    }
  });
  // Release anywhere ends a pending press (a short click), clearing the timer before it fires.
  // `opened` is deliberately *not* reset here - the renderer's `click` fires after this
  // `pointerup` and must still read the latch to skip; the next press resets it.
  window.addEventListener('pointerup', (e) => {
    if (e.pointerType === 'touch') return;
    if (e.button !== 0) return;
    cancelLongPress();
  });
  // Admin teleport tile-pick (ADR-0183): when the teleport cursor is armed, a left-click
  // on the ground converts to a tile and sends AdminTeleport, then disarms. Desktop has
  // no left-click-to-walk (movement is right-drag/keys), so a dedicated left-click here
  // never collides with movement; a left-click that *opened* the long-press menu is
  // skipped via the shared `longPress.opened` latch. The server re-authorizes + validates
  // the tile, so a forged send from a non-admin (who can't arm this) is a no-op anyway.
  app.canvas.addEventListener('click', (e) => {
    if (!teleportArmed || e.button !== 0 || !renderer || playerId === undefined) return;
    if (longPress.opened) return; // a long-press isn't a teleport click
    const r = canvasOffset();
    const t = renderer.screenToGroundTile(e.clientX - r.left, e.clientY - r.top);
    activeClient?.adminTeleport(Math.round(t.x), Math.round(t.y));
    disarmTeleportTargeting();
    e.preventDefault();
  });
  app.canvas.addEventListener('mousedown', (e) => {
    if (e.button !== 2) return; // right button only
    // Right-click is movement-only (ADR-0161): a right-press always drives a walk toward the
    // cursor, even over a sprite. Empty ground or a nameless mobile (e.g. a giant rat) walks
    // exactly as before; the context menu is no longer on right-click, so nothing is
    // suppressed here.
    mouseHeld = true;
    updateCursor(e);
    e.preventDefault();
  });
  app.canvas.addEventListener('mousemove', (e) => {
    // Track hover even before the right button is pressed. The gauntlet preview
    // reads this position independently of movement input.
    updateCursor(e);
    if (!mouseHeld) return;
    // Safety net for any right-button release we never saw - its `mouseup` swallowed by
    // a native context menu, a drag that ended over another window, etc.: if the right
    // button is no longer down, park rather than autowalk on. `buttons` bit 2 is the
    // right button; `mouseHeld` is only ever set by a right-press, so this can't misfire
    // during a genuine hold (bit 2 stays set) - it fires exactly when the latch outlived
    // the button.
    if ((e.buttons & 2) === 0) {
      mouseHeld = false;
      return;
    }
  });
  // Release anywhere ends the hold, so dragging the button off the canvas before
  // letting go still parks the player cleanly rather than walking on forever.
  window.addEventListener('mouseup', (e) => {
    if (e.button === 2) mouseHeld = false;
  });

  // Touch movement is owned by the lower-right joystick. Canvas taps are reserved
  // for interaction and targeting, while the route controller remains the shared
  // walk-up seam for interactions such as pursuing a rat.
  //
  // The client plans *and* executes: the route drives the prediction walker
  // step-by-step from its true tile, so arrival is exact and orbit-free by
  // construction (the latency artifacts ADR-0101's server-side `WalkPath`
  // execution fixed cannot occur under prediction - the walker parks itself
  // exactly on the last tile; the server merely validates each step).
  //
  // The `autowalk` controller is the thin, untested glue: it holds the active route
  // and re-path state and calls the pure functions. `intent()` is polled each tick
  // by the seam; `retarget()` is fired by an interaction pursuit; `cancel()` stops.
  let awPath: PathTile[] = [];
  const awRepath = createRepathState();
  let awActive = false;
  let awPrevTile: PathTile | undefined;
  // The step decision observes the *prediction walker*, not the snapshot: the
  // avatar now advances a tile the instant the walker completes a step. Mid-step
  // frames hold the last heading; a parked walker (arrived, or blocked and
  // refusing to step) is re-evaluated at the 20Hz sim-tick cadence
  // (STEP_MS) so the stall/re-path counters keep their #164 tuning.
  let awLastEvalAt = -1;
  const playerTileNow = (): PathTile | undefined => {
    // The walker's resting tile is where the player *is* under prediction; the
    // snapshot pose (which lags by RTT) is only the pre-entry fallback.
    if (walker) return { x: walker.tileX, y: walker.tileY };
    if (playerId === undefined) return undefined;
    const pos = latestWorld?.get(playerId)?.position;
    return pos ? { x: Math.round(pos.x), y: Math.round(pos.y) } : undefined;
  };
  // The walkability the local player routes through: their own level's rules
  // (roof-plate cells when standing on the roof, the door-aware ground mask
  // otherwise), matching what the server will accept for their steps.
  const playerWalkable = (world: NonNullable<typeof latestWorld>) => {
    const z = playerLevel(world);
    return walkableForLevel(world, z);
  };
  // The local player's LEVEL (0 = ground): the walker's z is already a level
  // (its input seams normalize), the snapshot fallback normalizes a mirrored
  // terrain z through GameMap.levelAt (PRD #536 Slice B).
  const playerLevel = (world: NonNullable<typeof latestWorld>): number => {
    if (walker) return walker.z;
    if (playerId === undefined) return 0;
    const pos = world.get(playerId)?.position;
    return pos ? world.map.levelAt(pos.x, pos.y, pos.z) : 0;
  };
  // The edge-based climb rule for route planning (PRD #536 Slice B): the map's
  // canStep on the ground level, none on a roof plate (flat surfaces). On a
  // flat map canStep is always-true, so interaction walk-up routes are unchanged.
  const playerCanStep = (world: NonNullable<typeof latestWorld>) =>
    playerLevel(world) === 0
      ? (from: PathTile, to: PathTile) => world.map.canStep(from, to)
      : null;
  // The predicate the walker predicts through, cached per adopted snapshot and
  // level (doors change it every snapshot; a teleporter landing changes the z).
  // Keyed on the snapshot *timestamp* as well as the world: since the incremental
  // replica (ADR-0324) the same World object is mutated in place across ordinary
  // deltas, so world identity alone would go stale when a door toggles - the
  // stamp re-keys the cache on every adopted snapshot exactly as the old
  // fresh-world-per-delta identity did.
  let walkerWalkableCache: { world: World; stamp: number; z: number; fn: IsWalkable } | undefined;
  const walkerWalkable = (world: World, z: number): IsWalkable => {
    // Admin no-clip mirror (ADR-0183): while no-clip is on, predict through the same
    // in-bounds-only rule the server's validateWalk applies, so a wall-step succeeds
    // locally instead of being refused and then rubber-banding. This is a pure client
    // mirror - the server remains the authority; if `clientNoClip` ever disagreed with
    // the server's session flag (e.g. a forged toggle on a non-admin), the server
    // rejects the wall-step and the existing onReject snap corrects it. Bypasses the
    // (world, z)-keyed cache deliberately, since the predicate no longer depends on z.
    if (clientNoClip) return (x, y) => world.map.inBounds(x, y);
    if (
      !walkerWalkableCache ||
      walkerWalkableCache.world !== world ||
      walkerWalkableCache.stamp !== lastSnapshotAt ||
      walkerWalkableCache.z !== z
    ) {
      walkerWalkableCache = { world, stamp: lastSnapshotAt, z, fn: walkableForLevel(world, z) };
    }
    const cell = walkerWalkableCache.fn;
    // Ground-level prediction also honours the edge-based climb rule (PRD #536
    // Slice B), closed over the walker's live resting tile - the movement
    // system's own pattern (ADR-0263) - so a cliff step is refused locally
    // instead of predicted and then rubber-banded by the server's validateWalk.
    // Roof plates are flat (no climb); flat maps degenerate to the cell rule.
    if (z !== 0 || !walker) return cell;
    const w = walker;
    return (x, y) => cell(x, y) && world.map.canStep({ x: w.tileX, y: w.tileY }, { x, y });
  };
  const autowalk = {
    /**
     * An interaction resolved a walk-up destination. Two independent knobs govern
     * its feedback:
     *
     *  - `quiet` skips the confirmation *ping*. The rat pursuit's per-wander
     *    re-route sets it: a chased rat re-routes every few hundred ms, and a ping
     *    each time would strobe.
     *  - `marker` (default `true`) shows/tracks the persistent "you're walking
     *    here" diamond. A pursuit passes the modality: a *touch* walk-up shows it
     *    (mobile feedback), while a *desktop* click walk-up hides it. `false` or an
     *    empty route clears any standing marker.
     */
    retarget(tappedTile: PathTile, opts?: { quiet?: boolean; marker?: boolean }): void {
      if (!renderer || !latestWorld) return;
      const isWalkable = playerWalkable(latestWorld);
      // An open-plain world (the endless arena, PRD #387) is unbounded: no clamp.
      const bounds = latestWorld.map.isPlain
        ? undefined
        : { width: latestWorld.map.width, height: latestWorld.map.height };
      const dest = resolveTarget(tappedTile, isWalkable, bounds);
      const start = playerTileNow();
      if (dest === null || !start) {
        // No reachable target: flash the "can't get there" signal on the tapped tile
        // and stop (cancel() also clears any standing destination marker). #163.
        renderer.signalBlocked(tappedTile);
        this.cancel();
        return;
      }
      const canStep = playerCanStep(latestWorld);
      awPath = findPath(start, dest, isWalkable, canStep ?? undefined);
      // Arm the re-path seam for this leg: same goal across reroutes, the live
      // door-aware predicate (refreshed each snapshot in intent()), the climb
      // rule for the level (PRD #536 Slice B), a fresh budget and a cleared
      // blocked flag (#164).
      awRepath.stalls = 0;
      awRepath.repaths = 0;
      awRepath.blocked = false;
      awRepath.goal = dest;
      awRepath.walkable = isWalkable;
      awRepath.canStep = canStep;
      awRepath.path = awPath;
      // Leave awPrevTile undefined so the first evaluation of the leg reads as
      // "moved" (a fresh start, not a stall) and force a recompute next frame.
      awPrevTile = undefined;
      awLastEvalAt = -1;
      awActive = awPath.length > 0; // an empty walk-up route is an immediate stop
      // Route-driven prediction (ADR-0166): hand the whole planned route to the
      // walker, which follows it tile-to-tile via `Mobile.setPath` and parks exactly
      // on the last tile - so the walk tracks `findPath`'s diagonal-then-straight
      // line with no held-intent overshoot/zig-zag. A reroute below re-issues a fresh
      // route the same way; a stop or empty route clears it.
      if (awActive) walker?.setPath(awPath);
      else walker?.clearPath();
      // Confirm the resolved tile with a ping unless quiet (see the pursuit re-route
      // note above). The persistent marker pins a touch walk-up and stays hidden for
      // a desktop walk-up (issue #163, ADR-0120).
      if (!opts?.quiet) renderer.pingDestination(dest);
      if ((opts?.marker ?? true) && awActive) renderer.markDestination(dest);
      else renderer.clearDestination();
    },
    /** Abandon any active route and park on the next tile boundary. */
    cancel(): void {
      awActive = false;
      awPath = [];
      // Leave route-driven mode so the next held direction (manual steering / typing)
      // drives the walker again (ADR-0166); idempotent while already idle.
      walker?.clearPath();
      // The trip is over (arrival, retarget-to-stop, or a manual/typing override):
      // drop the persistent destination marker (issue #163).
      renderer?.clearDestination();
    },
    /**
     * Per-frame route monitor (ADR-0166). The walker follows the whole route itself
     * (handed to it by `retarget` via `walker.setPath`), so this no longer emits a
     * per-frame direction - it only watches for the seams: arrival (clean stop) and
     * a re-path trigger (the world changed under the walk - re-plan and re-issue the
     * route). Evaluated the moment the walker lands on a fresh tile (progress) and,
     * while parked, at the 20Hz cadence the old snapshot-driven decision ran at, so
     * the stall/re-path counters keep their #164 tuning.
     */
    tick(): void {
      if (!awActive) return;
      const cur = playerTileNow();
      if (!cur) return;
      const moved = !awPrevTile || awPrevTile.x !== cur.x || awPrevTile.y !== cur.y;
      const nowMs = performance.now();
      const midStep = walker !== undefined && walker.isMoving;
      if (!(moved || (!midStep && nowMs - awLastEvalAt >= STEP_MS))) return;
      awLastEvalAt = nowMs;
      awPrevTile = cur;
      // Rebuild the door-aware predicate from the latest world so a door that shut
      // mid-walk is seen by both the next-tile check and any reroute (#164). The
      // climb rule refreshes with it (a teleporter landing changes the level).
      awRepath.walkable = latestWorld ? playerWalkable(latestWorld) : null;
      awRepath.canStep = latestWorld ? playerCanStep(latestWorld) : null;
      const decision = nextRoute(cur, awPath, moved, awRepath);
      awPath = awRepath.path; // adopt any reroute nextRoute chose
      if (decision === 'arrived') {
        this.cancel();
        return;
      }
      if (decision === 'blocked') {
        // A can't-reach stop (cap exceeded or no route) flashes the "can't get
        // there" signal on the goal, then parks (#163/#164).
        renderer?.signalBlocked(awRepath.goal ?? cur);
        this.cancel();
        return;
      }
      if (decision !== 'walking') {
        // Re-path: the world changed under the walk. Push the fresh route to the
        // walker; it re-syncs onto it and carries on (the mobile parks exactly on
        // the last tile). `walking` needs nothing - the mobile drives itself.
        walker?.setPath(decision.route);
      }
    },
  };

  // Rat-pursuit driver (Rat Swarm Slice E, ADR-0120): called each ticker frame
  // while no manual steering/typing overrides. The pure brain (input/pursuit.ts)
  // decides; this glue re-derives the rat's *current* tile from the read-model
  // world (rats wander), re-routes the same autowalk controller taps use (quiet,
  // so the overlay marker tracks the rat without a ping strobe), and fires the
  // pursuit's single Stomp once within Chebyshev 2. Kills are never predicted:
  // the stomp rides the command envelope and the authoritative delta despawns
  // the rat (or doesn't - a lost race just ends with the die-cancel above).
  const pursueRat = (): void => {
    if (pursuit.target === null || playerId === undefined) return;
    const pos = latestWorld?.get(pursuit.target)?.position;
    const ratTile = pos ? { x: Math.round(pos.x), y: Math.round(pos.y) } : undefined;
    const decision = pursuitStep(pursuit, playerTileNow(), ratTile);
    switch (decision.kind) {
      case 'chase':
        autowalk.retarget(decision.tile, { quiet: true, marker: pursuitViaTouch });
        break;
      case 'stomp':
        // In range: park and fire this pursuit's one Stomp. `warMode: true` is
        // the client-local stance assertion (the click that began the pursuit
        // was war-gated, and war-off cancels pursuit); the server still
        // re-validates range/liveness against authoritative state - silently,
        // so a lost race costs nothing.
        autowalk.cancel();
        activeClient?.sendCommand({
          kind: 'Stomp',
          entity: playerId,
          rat: decision.rat,
          warMode: true,
        });
        break;
      case 'vanished':
      case 'hold':
        // The rat left the world (despawn backstop), or the stomp is already
        // fired and we are in range: stop walking and await the outcome.
        autowalk.cancel();
        break;
      case 'idle':
      case 'follow':
        break; // no pursuit / keep walking the route already ordered
    }
  };
  // Unified canvas-touch state machine (issue #170, PRD #169). One finger taps or
  // long-presses to interact; two fingers pinch-to-zoom. The two interactions are
  // mutually exclusive
  // **by construction** via a single `mode`:
  //   - `idle`          : no touches down.
  //   - `tap-candidate` : exactly one touch, started from a zero-touch state, still
  //                       eligible to commit an interaction (single pointer, <~10px, <~300ms).
  //   - `pinch`         : a second finger landed; drives the zoom. **Latches** until
  //                       *all* fingers lift, so lifting out of a pinch never falls
  //                       back into a tap (the "lift-out-of-pinch becomes a tap"
  //                       hazard the PRD calls out).
  // A tap may begin only on the 0->1 transition; a pinch holds the machine until 0.
  // `points` tracks every live touch pointer's current canvas position so the pinch
  // span can be recomputed on each move.
  const TAP_MOVE_PX = 10;
  const TAP_MS = 300;
  type TouchMode = 'idle' | 'tap-candidate' | 'pinch';
  const touch = {
    mode: 'idle' as TouchMode,
    points: new Map<number, { x: number; y: number }>(),
    // tap-candidate bookkeeping
    tapPointerId: undefined as number | undefined,
    tapStart: undefined as { x: number; y: number; t: number } | undefined,
    // pinch bookkeeping: the two driving pointer ids + the baseline captured when
    // the second finger landed (gesture span and the zoom target it scales from).
    pinchA: undefined as number | undefined,
    pinchB: undefined as number | undefined,
    baselineDist: 0,
    baselineZoom: 1,
  };
  const canvasPoint = (e: PointerEvent): { x: number; y: number } => {
    const r = canvasOffset();
    return { x: e.clientX - r.left, y: e.clientY - r.top };
  };
  app.canvas.addEventListener('pointerdown', (e) => {
    if (e.pointerType !== 'touch') return; // mouse/pen keep the desktop scheme
    const fromZero = touch.points.size === 0;
    const p = canvasPoint(e);
    touch.points.set(e.pointerId, p);
    if (touch.points.size >= 2 && touch.mode !== 'pinch') {
      // A second finger arrived: enter pinch, abandoning any tap candidate. Capture
      // the baseline span between the two live touches and the zoom we scale from.
      const ids = [...touch.points.keys()];
      touch.pinchA = ids[0]!;
      touch.pinchB = ids[1]!;
      const a = touch.points.get(touch.pinchA)!;
      const b = touch.points.get(touch.pinchB)!;
      touch.baselineDist = pinchDistance(a, b);
      touch.baselineZoom = renderer ? renderer.zoomTarget() : 1;
      touch.mode = 'pinch';
      touch.tapPointerId = undefined;
      touch.tapStart = undefined;
      cancelLongPress(); // a second finger abandons any pending press-and-hold menu
      e.preventDefault(); // belt-and-suspenders with `touch-action: none` on the canvas
      return;
    }
    if (touch.mode === 'pinch') return; // extra fingers during a latched pinch: ignore
    // A single finger from a zero-touch state: arm a tap candidate. (A lone finger
    // left over from a drained pinch can't reach here - the pinch latches to 0.)
    if (fromZero) {
      touch.mode = 'tap-candidate';
      touch.tapPointerId = e.pointerId;
      touch.tapStart = { x: p.x, y: p.y, t: performance.now() };
      // A touch press-and-hold is the mobile context-menu gesture (ADR-0169): arm the same
      // long-press timer the desktop path uses. If the finger stays still for `LONG_PRESS_MS`
      // the menu opens and `longPress.opened` latches, so the release below does not walk.
      armLongPress(p.x, p.y, e.clientX, e.clientY);
    }
  });
  app.canvas.addEventListener('pointermove', (e) => {
    if (e.pointerType !== 'touch') return;
    if (!touch.points.has(e.pointerId)) return;
    const moved = canvasPoint(e);
    touch.points.set(e.pointerId, moved);
    // A tap candidate that drifts past tolerance is a drag, not a press-and-hold: abandon the
    // pending menu so it never opens mid-scroll. (The tap-vs-walk commit still checks at release.)
    if (
      touch.mode === 'tap-candidate' &&
      e.pointerId === touch.tapPointerId &&
      longPress.start !== undefined &&
      movedBeyondTolerance(longPress.start, moved)
    ) {
      cancelLongPress();
    }
    if (touch.mode !== 'pinch') return; // tap-candidate moves are handled at commit
    if (touch.pinchA === undefined || touch.pinchB === undefined) return;
    const a = touch.points.get(touch.pinchA);
    const b = touch.points.get(touch.pinchB);
    if (!a || !b) return; // a driving finger lifted: hold zoom until the pinch drains
    e.preventDefault();
    if (renderer) {
      // Pure ratio -> the renderer clamps/eases; no new zoom state, player stays centred.
      renderer.setZoom(zoomForPinch(touch.baselineZoom, touch.baselineDist, pinchDistance(a, b)));
    }
  });
  app.canvas.addEventListener('pointerup', (e) => {
    if (e.pointerType !== 'touch') return;
    touch.points.delete(e.pointerId);
    const committingTap =
      touch.mode === 'tap-candidate' && e.pointerId === touch.tapPointerId && touch.tapStart;
    const start = touch.tapStart;
    // Did this press already open the context menu via long-press? Capture the latch before
    // clearing the timer, so a released hold suppresses the walk/pursue exactly like a desktop
    // long-press suppresses the click.
    const longPressOpened = longPress.opened;
    cancelLongPress();
    // When the last finger lifts, the machine returns to idle ready for a fresh tap;
    // a pinch with fingers still down stays latched (never decays into a tap).
    if (touch.points.size === 0) {
      touch.mode = 'idle';
      touch.tapPointerId = undefined;
      touch.tapStart = undefined;
      touch.pinchA = undefined;
      touch.pinchB = undefined;
    }
    if (longPressOpened) {
      longPress.opened = false; // consumed: the menu opened, so this touch does not walk
      return;
    }
    if (!committingTap || !start) return; // not the tracked single-finger tap
    const p = canvasPoint(e);
    const movedPx = Math.hypot(p.x - start.x, p.y - start.y);
    const heldMs = performance.now() - start.t;
    if (movedPx > TAP_MOVE_PX || heldMs > TAP_MS) return; // a drag or a long hold
    if (playerId === undefined || !renderer) return;
    // Admin teleport tile-pick on touch (ADR-0183): an armed teleport tap sends
    // AdminTeleport for that tile instead of walking there, then disarms. Checked before
    // the rat/ground walk so it takes the tap.
    if (teleportArmed) {
      const dest = renderer.screenToGroundTile(p.x, p.y);
      activeClient?.adminTeleport(Math.round(dest.x), Math.round(dest.y));
      disarmTeleportTargeting();
      return;
    }
    // A tap on a targetable rat (war mode) begins the same walk-up-and-stomp
    // pursuit a desktop left-click does - mobile has no separate click, so the
    // tap is the one gesture and must route to the rat rather than the ground
    // under it (a rat's art sits above its foot tile). `pickRat` is war-gated and
    // corpse-filtered, so a peace-mode tap or a tap on a corpse falls through to
    // the ground walk below.
    const rat = renderer.pickRat(p.x, p.y);
    if (rat !== undefined) {
      pursuitViaTouch = true; // a mobile tap: show the walk-up marker
      targetRat(pursuit, rat, dyingRats);
      return;
    }
    // Empty ground intentionally does nothing. Touch movement now belongs only to
    // the joystick, leaving the full canvas tap gesture free for world interactions.
  });
  app.canvas.addEventListener('pointercancel', (e) => {
    if (e.pointerType !== 'touch') return;
    touch.points.delete(e.pointerId);
    cancelLongPress(); // a cancelled touch drops any pending press-and-hold menu
    if (touch.points.size === 0) {
      touch.mode = 'idle';
      touch.tapPointerId = undefined;
      touch.tapStart = undefined;
      touch.pinchA = undefined;
      touch.pinchB = undefined;
    }
  });

  // Mousewheel zoom (issue #67). Each notch nudges the renderer's zoom *target*;
  // the renderer eases the applied zoom toward it each frame, so the view glides
  // in/out rather than stepping. Wheel up (deltaY < 0) zooms in, down zooms out.
  // `passive: false` + `preventDefault` keeps the page from scrolling under the
  // canvas. The renderer clamps to its min/max, so spinning the wheel saturates.
  app.canvas.addEventListener(
    'wheel',
    (e) => {
      e.preventDefault();
      const notches = -Math.sign(e.deltaY);
      if (renderer && notches !== 0) renderer.adjustZoom(notches);
    },
    { passive: false },
  );

  // On refocus, drop any overhead speech that piled up while the tab was hidden.
  // The rAF-backed ticker (below) is paused on a hidden tab so lines never age out,
  // while the socket keeps delivering barks - so without this the whole hidden-period
  // backlog would appear at once and fade together (issue #182). Intake is already
  // gated on `document.hidden` in the chat installer's speech handler, so this only
  // sweeps up the few lines that could slip through before the browser fully pauses;
  // together they guarantee an empty overhead surface on return. The journal is untouched.
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') renderer?.clearAllSpeech();
    // Tab hidden: a focus-stealing combo that hid the tab likely swallowed the keyup/
    // mouseup too, so drop every held-input latch (the blur twin) - otherwise the player
    // returns to a character still walking/running with nothing held.
    else releaseAllInput();
  });

  // Render loop. The browser owns no tick: it predicts the local player through
  // the walker (sending its per-step `Walk` requests), interpolates the read-model
  // between the last two received snapshots for everyone else, and draws. `alpha`
  // is the fraction of a fixed step elapsed since the last snapshot (clamped to 1
  // so a late snapshot holds rather than overshoots).
  //
  // The gauntlet heading currently painted (idle = null). Tracked so the
  // directional cursor is rewritten only when it changes (#66) - no per-frame
  // style.cursor churn, so the OS cursor stays lag-free.
  let lastCursorDir: Direction | null = null;

  // The war-mode toggle (Rat Swarm Slice E, ADR-0120), fired by the Tab action
  // registered above (toggle-style: keydown flips, no hold). Three client-local
  // effects, nothing on the wire: the renderer's rat hover/click gate arms (or
  // sweeps its highlights), the gauntlet swaps to its red war variant immediately
  // (the ticker only rewrites the cursor on a *heading* change, so the toggle must
  // repaint here), and dropping out of war abandons any rat pursuit - the stance
  // gate is what made the chase legal.
  // The active cursor treatment (peace / war / duel). A live duel forces the
  // war-red cursor for its whole duration (ADR-0131), outranking the local Tab
  // war-mode toggle so a participant is unmistakably "in a fight"; then the duel
  // target cursor while armed (a transient targeting state), then the Tab war
  // mode, else the plain gauntlet - so the 0x2076 targeting gauntlet shows the
  // instant the phrase arms it. `duelCombat` is a separate flag from `warMode`, so forcing
  // it never disturbs the player's own stance - it reverts cleanly at duel end.
  // The admin teleport tile cursor (ADR-0183) reuses the duel target reticle (UO
  // 0x2076) - both are "click a target" affordances - so it needs no new cursor art,
  // and it outranks the plain gauntlet like the duel cursor does. It does NOT force war
  // (unlike a live duel), and it is a *tile* picker, so it does not arm the renderer's
  // entity hover/click wiring (that is `duelArmed`).
  const cursorMode = (): CursorMode =>
    duelCombat ? 'war' : duelArmed || teleportArmed ? 'duel' : warMode ? 'war' : 'peace';

  toggleWarMode = (): void => {
    // Tab cancels a pending duel call-out or an armed teleport cursor (the targeting
    // cursors are mutually exclusive), then toggles war mode as usual.
    disarmDuelTargeting();
    disarmTeleportTargeting();
    warMode = !warMode;
    renderer?.setWarMode(warMode);
    setDirectionalCursor(host, lastCursorDir, cursorMode());
    if (!warMode && pursuit.target !== null) {
      cancelPursuit(pursuit);
      autowalk.cancel();
    }
  };

  // Arm the blue player-target cursor for a given purpose (Player duels ADR-0130 / Dice
  // Wager ADR-0154 / `/ignore` ADR-0185): enable the renderer's blue player-hover/click
  // wiring and swap in the UO targeting gauntlet (0x2076) immediately (the ticker only
  // rewrites the cursor on a heading change, so arm/disarm repaint here). The cursor +
  // hover behaviour is identical for every purpose; `duelTargetPurpose` records which
  // installer's pick handler a pick routes to.
  const armTargeting = (purpose: TargetPurpose): void => {
    if (playerId === undefined) return;
    // The entity target cursor and the admin teleport tile cursor are mutually
    // exclusive (ADR-0183): arming one disarms the other, so at most one is active.
    disarmTeleportTargeting();
    duelTargetPurpose = purpose;
    if (duelArmed) return;
    if (warMode) {
      warMode = false;
      renderer?.setWarMode(false);
      if (pursuit.target !== null) {
        cancelPursuit(pursuit);
        autowalk.cancel();
      }
    }
    duelArmed = true;
    renderer?.setDuelTargeting(true);
    setDirectionalCursor(host, lastCursorDir, cursorMode());
  };
  // Disarm the target cursor (after a pick, on cancel, or when Tab flips war): sweep the
  // blue hover wiring, reset the purpose to the duel default, and revert the cursor.
  const disarmDuelTargeting = (): void => {
    if (!duelArmed) return;
    duelArmed = false;
    duelTargetPurpose = 'duel';
    renderer?.setDuelTargeting(false);
    setDirectionalCursor(host, lastCursorDir, cursorMode());
  };

  // Arm the admin teleport tile cursor (ADR-0183): the next left-click / touch tap on
  // the ground sends AdminTeleport for that tile (the server re-authorizes + validates).
  // A *tile* picker, so - unlike the entity duel/ignore cursor - it does not touch the
  // renderer's blue player-hover wiring; it just swaps the reticle via cursorMode(). Arms
  // only for a revealed admin; the server gates the power regardless.
  const armTeleportTargeting = (): void => {
    if (playerId === undefined || !isAdminViewer) return;
    disarmDuelTargeting(); // mutually exclusive with the entity target cursor
    if (teleportArmed) return;
    if (warMode) {
      warMode = false;
      renderer?.setWarMode(false);
      if (pursuit.target !== null) {
        cancelPursuit(pursuit);
        autowalk.cancel();
      }
    }
    teleportArmed = true;
    setDirectionalCursor(host, lastCursorDir, cursorMode());
  };
  const disarmTeleportTargeting = (): void => {
    if (!teleportArmed) return;
    teleportArmed = false;
    setDirectionalCursor(host, lastCursorDir, cursorMode());
  };

  let presentationSampleAt = performance.now();
  let arenaPerformanceActive = false;
  let observedLongTasks = 0;
  let renderSubmissionStartedAt = 0;
  app.ticker.add(
    () => {
      renderSubmissionStartedAt = performance.now();
    },
    undefined,
    UPDATE_PRIORITY.LOW + 1,
  );
  app.ticker.add(
    () => {
      if (renderSubmissionStartedAt > 0) {
        performanceMonitor.record(
          'render-submission',
          performance.now() - renderSubmissionStartedAt,
        );
      }
    },
    undefined,
    UPDATE_PRIORITY.LOW - 1,
  );

  app.ticker.add((ticker) => {
    performanceMonitor.record('frame', ticker.deltaMS);
    if (playerId !== undefined) {
      // While typing, force "no input" even if a movement key is still physically
      // held from before the input was focused, so the character halts rather than
      // walking on under the chat box. (The chat installer provides the focus poll.)
      const typing = chatFocusProvider();
      // Keyboard takes precedence when a key is held; otherwise a held right mouse
      // button walks toward the cursor's current 8-way direction. Both feed the one
      // prediction walker below, so the heading updates as the cursor crosses an
      // octant boundary and a release falls back to keyboard (or to a clean `null`
      // stop).
      const keyDir = typing ? null : heldInput.direction();
      const joystickDir = typing ? null : joystick.direction();
      // The held cursor's offset from the player's on-screen centre drives an
      // 8-way heading, a turn-only spin (cursor near the feet) and a run (cursor
      // far out) - ClassicUO's angle/radius model (see ADR-0022).
      const mouse =
        !typing && mouseHeld && cursor && renderer
          ? directionFromCursor(renderer.playerCenter(), cursor)
          : null;
      // Movement-intent source seam: exactly one source drives the prediction walker
      // each tick. Keyboard wins over the mobile joystick, which wins over a held
      // right mouse button. Any manual source cancels an interaction walk-up route.
      const manualActive = !typing && (keyDir !== null || joystickDir !== null || mouseHeld);
      let dir: Direction | null;
      let turnOnly: boolean;
      let running: boolean;
      if (manualActive) {
        autowalk.cancel();
        // Manual steering retakes control: abandon any rat pursuit (PRD story 35).
        cancelPursuit(pursuit);
        const useHeldDirection = keyDir !== null || joystickDir !== null;
        const dungeonMovement =
          latestWorld?.map.toData().dungeon !== undefined;
        dir = keyDir ?? joystickDir ?? mouse?.dir ?? null;
        turnOnly = useHeldDirection ? false : (mouse?.turnOnly ?? false);
        // Dungeon keyboard combat uses the action-mode run gait (ADR-0417).
        // Mouse and joystick keep their existing source-specific rules, while
        // "Always run" (ADR-0243) continues to force every manual source.
        running = manualRunning({
          dungeon: dungeonMovement,
          alwaysRun: alwaysRunSetting.isEnabled(),
          keyboard: keyDir !== null,
          keyboardRunning: heldInput.running(),
          joystick: joystickDir !== null,
          mouseRunning: mouse?.running ?? false,
        });
      } else if (typing) {
        autowalk.cancel();
        // Typing halts movement, and a pursuit is a movement commitment: drop it
        // too, exactly as an interaction walk-up route is dropped.
        cancelPursuit(pursuit);
        dir = null;
        turnOnly = false;
        running = false;
      } else {
        // Rat pursuit drives the same autowalk controller a tap does: re-route
        // to the rat's live tile / fire the one Stomp in range (Slice E), then
        // monitor the route for arrival / re-path (ADR-0166). The walker follows
        // the whole route itself via `Mobile.setPath`, so the ticker feeds no
        // per-frame direction here - the route was handed over at retarget/reroute.
        pursueRat();
        autowalk.tick();
        dir = null;
        turnOnly = false;
        running = false;
      }
      // Prediction: the walker pulls the held input at its own cadence (turn
      // beat, step chaining), moves the local character instantly, and emits one
      // `Walk` request per turn/step start - sent up the socket verbatim. The
      // walkability predicate matches the server's per-level rule and is cached
      // per adopted snapshot + level (doors change it; a teleporter changes z).
      if (walker && latestWorld) {
        const desired = { dir, running, turnOnly };
        const dungeonMovement =
          latestWorld.map.toData().dungeon !== undefined;
        if (dungeonMovement) {
          const messages = walker.updateDungeon(
            ticker.deltaMS,
            desired,
            latestWorld,
          );
          for (const msg of messages) {
            activeClient?.sendDungeonMoveIntent(msg);
          }
        } else {
          const messages = walker.update(
            ticker.deltaMS,
            desired,
            walkerWalkable(latestWorld, walker.z),
          );
          for (const msg of messages) activeClient?.sendWalk(msg);
        }
        // The renderer draws the own sprite from the prediction, not the lagging
        // snapshot: fractional tile, level, facing, and gait, camera included.
        const rp = walker.renderPosition();
        renderer?.setLocalPose({
          x: rp.x,
          y: rp.y,
          z: walker.z,
          facing: walker.facing,
          isMoving: walker.isMoving,
          isRunning: walker.isRunning,
        });
      }
      // Preview the right-click direction from hover alone. Keyboard, joystick,
      // route walking, and current movement state do not rotate the gauntlet.
      // The turn-only zone keeps the idle art because pressing there would pivot
      // in place rather than move. Rewrite only when the preview heading changes.
      const cursorDir = previewDirectionFromCursor(renderer?.playerCenter(), cursor);
      if (cursorDir !== lastCursorDir) {
        // In war mode the same heading paints the red war variant of the art (blue
        // while the duel cursor is armed); the toggles repaint immediately above.
        setDirectionalCursor(host, cursorDir, cursorMode());
        lastCursorDir = cursorDir;
      }
    }

    if (renderer && latestWorld) {
      renderer.sync(latestWorld, ticker.deltaMS);
      // First frame with a built renderer and a world to draw: the bank is genuinely
      // on screen now (assets loaded - the renderer is built from them - and the first
      // snapshot synced), so dismiss the loading overlay. Doing it *after* sync, in the
      // same frame Pixi then renders, avoids any flash of an empty/half-loaded map.
      dismissLoading();
    }

    const presentationNow = performance.now();
    if (renderer && presentationNow - presentationSampleAt >= 1_000) {
      presentationSampleAt = presentationNow;
      const workload = renderer.presentationWorkload();
      const active =
        workload.liveEnemies > 0 || workload.xpGems > 0 || workload.corpses > 0;
      if (active && !arenaPerformanceActive) {
        performanceMonitor.reset();
        const reset = presentationGovernor.reset(presentationNow);
        presentationTier = reset.tier;
        presentationReason = reset.reason;
        renderer.setPresentationQuality(presentationTier);
        observedLongTasks = 0;
      }
      arenaPerformanceActive = active;
      if (active) {
        const pressure = performanceMonitor.presentationPressureSnapshot();
        const frame = pressure.frame;
        const longTasks = pressure.longTasks;
        if (frame) {
          const decision = presentationGovernor.observe(
            {
              p95Ms: frame.p95Ms,
              p99Ms: frame.p99Ms,
              longTasks: Math.max(0, longTasks - observedLongTasks),
              targetFrameMs: 1_000 / presentationCadenceFps,
            },
            presentationNow,
          );
          presentationTier = decision.tier;
          presentationReason = decision.reason;
          if (decision.changed) renderer.setPresentationQuality(decision.tier);
        }
        observedLongTasks = longTasks;
      }
      performanceMonitor.updatePresentation({
        workload,
        quality: {
          tier: presentationTier,
          reason: presentationReason,
        },
        cadence: {
          state: presentationCadenceState,
          fps: presentationCadenceFps,
        },
      });
    }

    // Every installer's per-frame hook (board countdowns, the death cinematic, the
    // arena HUD/veil/heartbeat/combo) runs after the world sync, exactly where the
    // old inline blocks sat.
    for (const hook of frameHooks) hook(ticker.deltaMS);
    if (entered) {
      applyCadence(
        presentationCadence.evaluate(
          performance.now(),
          (renderer?.hasActivePresentation() ?? false) ||
            frameActivitySources.some((source) => source()),
        ),
      );
    }
  });

  // Everything above (renderer, render loop, the spectator backdrop) is live while
  // the splash is still up. Now wait for the visitor to submit the form, then open
  // the real connection with their chosen identity - which spawns their character,
  // retargets the camera, and retires the backdrop (see `connect`/`onWelcome`).
  const identity = await identityPromise;
  connect(identity);
}

if (import.meta.env.DEV && ARENA_PERFORMANCE_PARAMS.has('huePixelFixture')) {
  void import('./render/huePixelComparisonFixture').then(
    ({ runHuePixelComparisonFixture }) => runHuePixelComparisonFixture(),
  );
} else {
  void main();
}
